// src/pages/Creditors.jsx
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Autosuggest from 'react-autosuggest';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowDownAZ, CalendarDays, DollarSign, Home, StarIcon,Gavel, CreditCard, Sigma, Landmark, GraduationCap, Users } from "lucide-react";
import { Star as OutlineStar }       from "lucide-react";
import { StarIcon as SolidStar }     from "@heroicons/react/20/solid";
import { generateSchedulesPdfLocal} from './forms/ScheduleDEF'
import ProofOfClaimSection from './ProofOfClaimSection';
import MailingMatrixServiceSelector from './components/MailingMatrixServiceSelector';
import { primaryDebtor, secondaryFilingDebtor } from '../utils/debtors';
import { isStudentLoanCreditor, responsibilityGroup, summarizeCreditors } from '../utils/creditorSummaries';
const API_URL = process.env.REACT_APP_API_URL
const zipcodes = require('zipcodes');

// Normalize to lowercase for selects/conditionals
const lc = (v) => (v ?? '').toString().trim().toLowerCase();

// Money utils
const toMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '';
  return Number.parseFloat(n).toFixed(2);
};
const formatSummaryMoney = (value) => Number(value || 0).toLocaleString(
  undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 },
);
// Use fallback only when the value is truly empty/null/undefined, not when it's 0
const numOr = (v, fallback) => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
// Coercion helpers to avoid sending '' where the API expects numbers/ints or null
const nOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v));
const iOrNull = (v) => (v === '' || v === undefined || v === null ? null : (Number.isFinite(Number(v)) ? parseInt(v, 10) : null));
// Amortize monthly payment: principal P, annualRate% APR, months n
const computeMonthlyPayment = (principal, annualRatePct, months) => {
  const P = Number(principal) || 0;
  const n = Number(months) || 0;
  const r = (Number(annualRatePct) || 0) / 100 / 12;
  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return +(P / n).toFixed(2);
  const payment = P * r / (1 - Math.pow(1 + r, -n));
  return +payment.toFixed(2);
};

// Does this creditor have any Chapter 13 treatment data?
const hasCh13Data = (c = {}) => {
  const fields = [
    'chapter_13_secured_treatment', 'chapter_13_other_description', 'chapter_13_direct_pay',
    'chapter_13_maintenance_payment_amount', 'chapter_13_maintenance_arrearage_payment',
    'chapter_13_maintenance_arrearage_months', 'chapter_13_maintenance_arrearage_interest_rate',
    'chapter_13_cramdown_secured_amount', 'chapter_13_cramdown_interest_rate',
    'chapter_13_cramdown_months', 'chapter_13_cramdown_payment',
    'chapter_13_910_secured_amount', 'chapter_13_910_interest_rate',
    'chapter_13_910_months', 'chapter_13_910_payment',
    'chapter_13_lien_secured_amount', 'chapter_13_lien_other_lien_amount',
    'chapter_13_lien_interest_rate', 'chapter_13_lien_months', 'chapter_13_lien_payment',
    'chapter_13_third_party'
  ];
  return fields.some(f => c?.[f] !== null && c?.[f] !== undefined && c?.[f] !== '');
};

// Sum linked asset values (value_total) and exemptions
// Sum linked asset values & exemptions, reading from link OR caseData assets
const sumLinkedAssetValues = (assetLinks = [], caseData) => {
  const getAssetFromLink = (link) => {
    if (!caseData) return null;
    const isVehicle = link?.asset_type === 'vehicle';
    const id = String(link?.object_id);
    if (isVehicle) {
      return (caseData.vehicles || []).find(v => String(v.id) === id) || null;
    }
    return (caseData.real_properties || []).find(p => String(p.id) === id) || null;
  };

  return assetLinks.reduce((acc, link) => {
    const a = link?.asset || getAssetFromLink(link) || {};

    const rawVal =
      link?.value_ownership_portion ??
      link?.value_total ??
      link?.value ??
      link?.amount ??
      a.value_ownership_portion ??
      a.value_total ??
      a.market_value ??
      a.amount ??
      0;

    // Try multiple common exemption shapes
    let ex = 0;
    if (link?.exemption_amount != null) ex = link.exemption_amount;
    else if (a.exemption_amount != null) ex = a.exemption_amount;
    else if (a.exemptions_total != null) ex = a.exemptions_total;
    else if (Array.isArray(a.exemptions)) {
      ex = a.exemptions.reduce((s, e) => s + (Number(e?.amount) || 0), 0);
    }

    return {
      totalValue: acc.totalValue + (Number(rawVal) || 0),
      totalExemption: acc.totalExemption + (Number(ex) || 0),
    };
  }, { totalValue: 0, totalExemption: 0 });
};

// Get other creditors with any of the same linked assets, opened BEFORE this creditor
// Other secured creditors on the same asset(s) opened before the current one
const findOtherLienholders = (allCreditors, editingCreditor) => {
  const assetKey = (link) => `${lc(link?.asset_type)}:${String(link?.object_id)}`;
  const myLinks = (editingCreditor?.asset_links || []).map(assetKey);
  if (!myLinks.length) return [];
  const myOpened = editingCreditor?.date_opened ? new Date(editingCreditor.date_opened) : null;

  return (allCreditors || []).filter(c => {
    if (String(c.id) === String(editingCreditor.id)) return false;
    if (lc(c.debt_type) !== 'secured') return false;
    const cLinks = (c.asset_links || []).map(assetKey);
    const sharesAny = cLinks.some(id => myLinks.includes(id));
    if (!sharesAny) return false;
    if (myOpened && c.date_opened) {
      try { return new Date(c.date_opened) < myOpened; } catch { return true; }
    }
    // If dates missing, include as conservative assumption
    return true;
  });
};
function debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }
  const normalizeText = (s) => {
    const str = s ?? "";
    return str
      .trim()                 // strip leading/trailing whitespace
      .toLowerCase()          // lowercase
      .replace(/\s+/g, " ");  // collapse multi-spaces
  };
  
  const normalizeZip = (z = "") =>
    z.split("-")[0].trim();   // drop the +4

const GOVERNMENT_CREDITORS = [
  {
    id: "us-attorney-east-tennessee",
    account_name: "United States Attorney’s Office",
    street: "Howard H. Baker Jr. U.S. Courthouse",
    street2: "800 Market Street, Suite 211",
    city: "Knoxville",
    state: "TN",
    zip_code: "37902",
  },
  {
    id: "internal-revenue-service",
    account_name: "Internal Revenue Service",
    street: "Centralized Insolvency Operation",
    street2: "P.O. Box 7346",
    city: "Philadelphia",
    state: "PA",
    zip_code: "19101-7346",
  },
  {
    id: "small-business-administration",
    account_name: "United States Small Business Administration",
    street: "District Director",
    street2: "2 International Plaza Drive, Suite 500",
    city: "Nashville",
    state: "TN",
    zip_code: "37217",
  },
  {
    id: "usda-nashville",
    account_name: "United States Department of Agriculture",
    street: "579 Estes Kefauver Federal Bldg.",
    street2: "801 Broadway",
    city: "Nashville",
    state: "TN",
    zip_code: "37203",
  },
  {
    id: "usda-rural-development",
    account_name: "United States Department of Agriculture",
    street: "Rural Housing Service/Rural Development",
    street2: "Post Office Box 66889",
    city: "St. Louis",
    state: "MO",
    zip_code: "63166",
  },
  {
    id: "veterans-affairs",
    account_name: "United States Department of Veterans Affairs",
    street: "District Director",
    street2: "7100 Commerce Way",
    city: "Brentwood",
    state: "TN",
    zip_code: "37027",
  },
  {
    id: "department-of-interior",
    account_name: "United States Department of the Interior",
    street: "Office of the Solicitor",
    street2: "1849 C. Street NW",
    city: "Washington",
    state: "DC",
    zip_code: "20240",
  },
  {
    id: "department-of-energy",
    account_name: "United States Department of Energy",
    street: "Chief Counsel",
    street2: "P.O. Box 2001",
    city: "Oak Ridge",
    state: "TN",
    zip_code: "37831",
  },
  {
    id: "housing-and-urban-development",
    account_name: "United States Department of Housing & Urban Development",
    street: "John J. Duncan Federal Bldg., Drawer 300",
    street2: "710 Locust Street, Third Floor",
    city: "Knoxville",
    state: "TN",
    zip_code: "37902-2526",
  },
  {
    id: "department-of-education",
    account_name: "U.S. Department of Education",
    street: "Office of General Counsel",
    street2: "400 Maryland Avenue, SW",
    city: "Washington",
    state: "DC",
    zip_code: "20202",
  },
  {
    id: "securities-and-exchange-commission",
    account_name: "U.S. Securities and Exchange Commission",
    street: "Office of Reorganization",
    street2: "950 East Paces Ferry Road, Suite 900",
    city: "Atlanta",
    state: "GA",
    zip_code: "30326-1382",
  },
  {
    id: "social-security-administration",
    account_name: "Social Security Administration",
    street: "Attn: Bankruptcy",
    street2: "6401 Security Boulevard",
    city: "Baltimore",
    state: "MD",
    zip_code: "21235",
  },
  {
    id: "tennessee-department-of-revenue",
    account_name: "Tennessee Department of Revenue",
    street: "c/o Tennessee Attorney General's Office",
    street2: "P.O. Box 20207",
    city: "Nashville",
    state: "TN",
    zip_code: "37202-0207",
  },
];

export default function FirmCreditors({}) {
  async function handleGenerate(){
    try {
      // Refresh only the case details + creditors into a fresh data snapshot
      const token = localStorage.getItem("accessToken");
      const [caseRes, credRes, serviceRes] = await Promise.all([
        fetch(`${API_URL}/case/${caseId}/creditors/case-details/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/creditors/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/amended-schedule-service/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!caseRes.ok || !credRes.ok) {
        console.error("Failed to refresh case or creditors before generate");
      }

      const caseJson = caseRes.ok ? await caseRes.json() : (data?.caseInfo || {});
      const credJson = credRes.ok ? await credRes.json() : (data?.allDebts || []);
      let serviceJson = amendedScheduleService;
      if (serviceRes.ok) {
        serviceJson = await serviceRes.json();
      } else {
        const serviceError = await serviceRes.json().catch(() => ({}));
        serviceJson = {
          ...(amendedScheduleService || {}),
          load_error: serviceError.detail
            || `The notice docket list could not be loaded (${serviceRes.status}).`,
        };
      }
      setAmendedScheduleService(serviceJson);
      const preferredNotice = caseFilingNotice
        || data?.caseFilingNotice
        || serviceJson.notice_candidates?.find(candidate => candidate.has_pdf)
        || serviceJson.notice_candidates?.[0]
        || null;

      // Build the fresh `data` object expected by generators
      const freshData = {
        caseInfo: caseJson,
        debtsD: credJson.filter(d => d.debt_type === "secured"),
        debtsE: credJson.filter(d => d.debt_type === "priority"),
        debtsF: credJson.filter(d => d.debt_type === "unsecured"),
        debtsG: credJson.filter(d => d.debt_type === "executory"),
        notifyEF: credJson.filter(d => d.additional_notified),
        allDebts: credJson,
        // preserve any amended flags already stored in data
        amended: data?.amended ?? false,
        amendedReason: data?.amendedReason ?? '',
        amendedCreditors: data?.amendedCreditors ?? [],
        mailingMatrixService: data?.mailingMatrixService ?? null,
        amendedScheduleService: serviceJson,
        caseFilingNotice: preferredNotice,
      };

      // Update state non-blocking so UI stays in sync
      setData(freshData);
      // Build a simple address list for UI display
          const creditorAddresses = credJson.map(c => ({
            name: c.account_name || c.name,
            street1: c.street1 || '',
            street2: c.street2 || '',
            city: c.city || '',
            state: c.state || '',
            zip_code: c.zip_code || '',
            codebtor: c.codebtor || '',
            codebtor_street1:c.codebtor_street1 || '',
            codebtor_street2:c.codebtor_street2 || '',
            codebtor_state:c.codebtor_state || '',
            codebtor_zip_code:c.codebtor_zip_code|| '',
            codebtor_city:c.codebtor_city||'',
          }));
          setCreditors2(creditorAddresses);
      // Generate with the just-refreshed snapshot; other args come from UI state
      const [DEF, matrix] = await generateSchedulesPdfLocal(
        freshData,
        freshData.amended,
        selectedSchedules,
        creditorAddresses
      );

      if (DEF){
        const DEFlink = document.createElement('a');
        DEFlink.href = URL.createObjectURL(DEF);
        DEFlink.download = 'DEF';
        DEFlink.click();
      }
      if (matrix){
        const matrixLink = document.createElement('a');
        matrixLink.href = URL.createObjectURL(matrix);
        matrixLink.download = 'Matrix';
        matrixLink.click();
      }
    } catch (err) {
      console.error('Generate (with refresh) failed:', err);
      setAmendmentError(err.message || 'Could not generate schedules.');
      alert(err.message || 'Could not generate schedules.');
    }
  }
  // Handler to unlink asset from creditor
  const handleDeleteAssetLink = async (linkId) => {
    if (!window.confirm("Remove this asset link?")) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${editingCreditor.id}/asset-links/${linkId}/`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) {
      // Refresh creditor data after removal
      fetchCreditors();
      closeEditModal();
    } else {
      alert("Failed to unlink asset.");
    }
  };
  const [groupByType, setGroupByType] = useState(false);
  const [splitUnsecured, setSplitUnsecured] = useState(false);
  const [responsibilityFilter, setResponsibilityFilter] = useState('all');
  const [amended, setAmended] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAmendedModal, setShowAmendedModal] = useState(false);
  const [showCh13Options, setShowCh13Options] = useState(false);
  const [amendedReason, setAmendedReason] = useState('');
  const [amendStep, setAmendStep] = useState(1);
  const [amendedCreditors, setAmendedCreditors] = useState([]);
  const [mailingMatrixService, setMailingMatrixService] = useState(null);
  const [amendedScheduleService, setAmendedScheduleService] = useState({
    filing_debtors: [],
    notice_candidates: [],
    other_docket_candidates: [],
    load_error: '',
  });
  const [caseFilingNotice, setCaseFilingNotice] = useState(null);
  const [amendmentError, setAmendmentError] = useState('');
  const [selectedSchedules, setSelectedSchedules] = useState(['all']);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCodebtorModal, setShowCodebtorModal] = useState(false)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false) 
  const [creditors, setCreditors] = useState([]);
  const [creditors2, setCreditors2] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caseLoading, setCaseLoading] = useState(true);
  const token = localStorage.getItem('accessToken');
  const { caseId } = useParams();
  const [deletingIds, setDeletingIds] = useState([]);
  const [sortOption, setSortOption] = useState("name");
  const [showLinkAssetModal, setShowLinkAssetModal] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [linkableAssets, setLinkableAssets] = useState([]);
  const [linkableAssetsLoading, setLinkableAssetsLoading] = useState(true);
  const [linkableAssetsError, setLinkableAssetsError] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [data, setData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGovernmentCreditorModal, setShowGovernmentCreditorModal] = useState(false);
  const [selectedGovernmentCreditorIds, setSelectedGovernmentCreditorIds] = useState([]);
  const [addingGovernmentCreditors, setAddingGovernmentCreditors] = useState(false);
  const [governmentCreditorStatus, setGovernmentCreditorStatus] = useState("");
  const [linking, setLinking] = useState(false);
  const [updatingType, setUpdatingType] = useState(false);
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false);
  const [showAddPropertyForm, setShowAddPropertyForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [saving, setSaving] = useState(false);
  // in FirmCreditors.jsx, alongside your other useState’s:
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentCreditor, setPaymentCreditor]   = useState(null);
  const [payments, setPayments]                 = useState([]);
  const [newPayment, setNewPayment]             = useState({ payment: "", payment_date: "" });
  const [paymentsLoading, setPaymentsLoading]   = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [commonCreditors, setCommonCreditors] = useState([]);
  const [editingCreditor, setEditingCreditor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const openPaymentsModal = async (c) => {
    setPaymentCreditor(c);
    setPaymentModalOpen(true);
    setPaymentsLoading(true);
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${c.id}/payments/`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) setPayments(await res.json());
    setPaymentsLoading(false);
  };
  const clampToBalance = (val, form) => {
  const bal = Number(form?.balance) || 0;
  const num = Number(val) || 0;
  return +Math.min(num, bal).toFixed(2);
};
  const closePaymentsModal = () => {
    setPaymentModalOpen(false);
    setPaymentCreditor(null);
    setPayments([]);
  };
    // Keep editForm in sync with server-updated editingCreditor (Ch13 fields)
  useEffect(() => {
    if (!editingCreditor) return;
    // Only merge Ch13 fields we care about without blowing away in-progress edits elsewhere
    setEditForm(prev => ({
      ...prev,
      chapter_13_secured_treatment: editingCreditor.chapter_13_secured_treatment || prev.chapter_13_secured_treatment || '',
      chapter_13_other_description: editingCreditor.chapter_13_other_description ?? prev.chapter_13_other_description ?? '',
      chapter_13_direct_pay: !!editingCreditor.chapter_13_direct_pay,
      chapter_13_maintenance_payment_amount: editingCreditor.chapter_13_maintenance_payment_amount ?? prev.chapter_13_maintenance_payment_amount ?? '',
      chapter_13_maintenance_arrearage_payment: editingCreditor.chapter_13_maintenance_arrearage_payment ?? prev.chapter_13_maintenance_arrearage_payment ?? '',
      chapter_13_maintenance_arrearage_months: editingCreditor.chapter_13_maintenance_arrearage_months ?? prev.chapter_13_maintenance_arrearage_months ?? '',
      chapter_13_maintenance_arrearage_interest_rate: editingCreditor.chapter_13_maintenance_arrearage_interest_rate ?? prev.chapter_13_maintenance_arrearage_interest_rate ?? '',
      chapter_13_cramdown_secured_amount: editingCreditor.chapter_13_cramdown_secured_amount ?? prev.chapter_13_cramdown_secured_amount ?? '',
      chapter_13_cramdown_interest_rate: editingCreditor.chapter_13_cramdown_interest_rate ?? prev.chapter_13_cramdown_interest_rate ?? '',
      chapter_13_cramdown_months: editingCreditor.chapter_13_cramdown_months ?? prev.chapter_13_cramdown_months ?? '',
      chapter_13_cramdown_payment: editingCreditor.chapter_13_cramdown_payment ?? prev.chapter_13_cramdown_payment ?? '',
      chapter_13_910_secured_amount: editingCreditor.chapter_13_910_secured_amount ?? prev.chapter_13_910_secured_amount ?? '',
      chapter_13_910_interest_rate: editingCreditor.chapter_13_910_interest_rate ?? prev.chapter_13_910_interest_rate ?? '',
      chapter_13_910_months: editingCreditor.chapter_13_910_months ?? prev.chapter_13_910_months ?? '',
      chapter_13_910_payment: editingCreditor.chapter_13_910_payment ?? prev.chapter_13_910_payment ?? '',
      chapter_13_lien_secured_amount: editingCreditor.chapter_13_lien_secured_amount ?? prev.chapter_13_lien_secured_amount ?? '',
      chapter_13_lien_other_lien_amount: editingCreditor.chapter_13_lien_other_lien_amount ?? prev.chapter_13_lien_other_lien_amount ?? '',
      chapter_13_lien_interest_rate: editingCreditor.chapter_13_lien_interest_rate ?? prev.chapter_13_lien_interest_rate ?? '',
      chapter_13_lien_months: editingCreditor.chapter_13_lien_months ?? prev.chapter_13_lien_months ?? '',
      chapter_13_lien_payment: editingCreditor.chapter_13_lien_payment ?? prev.chapter_13_lien_payment ?? '',
      chapter_13_third_party: editingCreditor.chapter_13_third_party ?? prev.chapter_13_third_party ?? '',
    }));
  }, [editingCreditor]);
  useEffect(() => {
      async function fetchData() {
        try {
          const token = localStorage.getItem("accessToken");
          // Fetch case details and creditors in parallel
          const [caseRes, credRes, serviceRes] = await Promise.all([
            fetch(`${API_URL}/case/${caseId}/creditors/case-details/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/case/${caseId}/creditors/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/case/${caseId}/amended-schedule-service/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          if (!caseRes.ok || !credRes.ok) {
            console.error("Failed to fetch case or creditors");
            return;
          }
          const caseJson = await caseRes.json();
          const credJson = await credRes.json();
          let serviceJson;
          if (serviceRes.ok) {
            serviceJson = await serviceRes.json();
          } else {
            const serviceError = await serviceRes.json().catch(() => ({}));
            serviceJson = {
              filing_debtors: [],
              notice_candidates: [],
              other_docket_candidates: [],
              load_error: serviceError.detail
                || `The notice docket list could not be loaded (${serviceRes.status}).`,
            };
          }
          setAmendedScheduleService(serviceJson);
          const preferredNotice = serviceJson.notice_candidates?.find(
            candidate => candidate.has_pdf
          ) || serviceJson.notice_candidates?.[0] || null;
          setCaseFilingNotice(preferredNotice);
          // Populate data object
          setData({
            caseInfo: caseJson,
            debtsD: credJson.filter(d => d.debt_type === "secured"),
            debtsE: credJson.filter(d => d.debt_type === "priority"),
            debtsF: credJson.filter(d => d.debt_type === "unsecured"),
            debtsG: credJson.filter(d => d.debt_type === "executory"),
            notifyEF: credJson.filter(d => d.additional_notified),
            allDebts : credJson,
            amended: false,
            amendedReason: '',
            amendedCreditors: [],
            mailingMatrixService: null,
            amendedScheduleService: serviceJson,
            caseFilingNotice: preferredNotice,
          });
          // Build a simple address list for UI display
          const creditorAddresses = credJson.map(c => ({
            name: c.account_name || c.name,
            street1: c.street1 || '',
            street2: c.street2 || '',
            city: c.city || '',
            state: c.state || '',
            zip_code: c.zip_code || '',
            codebtor: c.codebtor || '',
            codebtor_street1:c.codebtor_street1 || '',
            codebtor_street2:c.codebtor_street2 || '',
            codebtor_state:c.codebtor_state || '',
            codebtor_zip_code:c.codebtor_zip_code|| '',
            codebtor_city:c.codebtor_city||'',
          }));
          setCreditors2(creditorAddresses);
        } catch (err) {
          console.error("Error fetching schedule data:", err);
        } finally {
          setLoading(false);
        }
      }
      fetchData();
    }, [caseId, API_URL]);
  const handleAddPayment = async (e) => {
    e.preventDefault();
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${paymentCreditor.id}/payments/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPayment),
      }
    );
    if (res.ok) {
      const created = await res.json();
      setPayments(p => [created, ...p]);
      setNewPayment({ payment: "", payment_date: "" });
    } else alert("Could not add payment");
  };
  
  const handleDeletePayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${paymentCreditor.id}/payments/${id}/`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) setPayments(p => p.filter(x => x.id !== id));
    else alert("Could not delete");
  };
  const openEditModal = (c) => {
    setEditingCreditor(c);
    const last = c.payments?.slice(-1)[0] || {};
    setEditForm({
      account_name: c.account_name,
      balance:      c.balance,
      arrearage:    c.arrearage,
      street1:       c.street1 || c.street1 || "",
      street2:      c.street2 || "",
      city:         c.city,
      state:        c.state,
      zip_code:     c.zip_code,
      debt_type:    c.debt_type,
      account_type: c.account_type,
      date_opened:  c.date_opened,
      subject_to_offset:  c.subject_to_offset,
      account_number: c.account_number,
      nature_of_lien: c.nature_of_lien || "agreement",
      lien_description: c.lien_description || "",
      lien_book: c.lien_book || "",
      lien_page: c.lien_page || "",
      lien_case_number: c.lien_case_number || "",
      lien_county: c.lien_county || "",
      authorized_user: c.authorized_user,
      dispute_status: c.dispute_status,
      who_owes:     c.who_owes,
      community_debt: c.community_debt,
      priority_amount: c.priority_amount,
      priority_type: c.priority_type,
      description_of_lease: c.description_of_lease,
      statement_of_intent_secured_treatment:c.statement_of_intent_secured_treatment,
      statement_of_intent_other_description:c.statement_of_intent_other_description,
      statement_of_intent_assumption:c.statement_of_intent_assumption,
      additional_notified:c.additional_notified,
      fdic_bank:c.fdic_bank,
      piti: c.piti,
      codebtor:            c.codebtor || "",
      codebtor_street1:    c.codebtor_street1 || "",
      codebtor_street2:    c.codebtor_street2 || "",
      codebtor_city:       c.codebtor_city || "",
      asset_links:  c.asset_links || [],
      codebtor_state:      c.codebtor_state || "",
      codebtor_zip_code:   c.codebtor_zip_code || "",
      payment:      last.payment     || "",
      payment_date: last.payment_date|| "",
      // ------- Chapter 13 fields -------
      chapter_13_secured_treatment: c.chapter_13_secured_treatment || "",
      chapter_13_other_description: c.chapter_13_other_description || "",
      chapter_13_direct_pay: !!c.chapter_13_direct_pay,

      // Maintenance
      chapter_13_maintenance_payment_amount: c.chapter_13_maintenance_payment_amount ?? "",
      chapter_13_maintenance_arrearage_payment: c.chapter_13_maintenance_arrearage_payment ?? "",
      chapter_13_maintenance_arrearage_months: c.chapter_13_maintenance_arrearage_months ?? "",
      chapter_13_maintenance_arrearage_interest_rate: c.chapter_13_maintenance_arrearage_interest_rate ?? "",

      // Cramdown
      chapter_13_cramdown_secured_amount: c.chapter_13_cramdown_secured_amount ?? "",
      chapter_13_cramdown_interest_rate: c.chapter_13_cramdown_interest_rate ?? "",
      chapter_13_cramdown_months: c.chapter_13_cramdown_months ?? "",
      chapter_13_cramdown_payment: c.chapter_13_cramdown_payment ?? "",

      // 910
      chapter_13_910_secured_amount: c.chapter_13_910_secured_amount ?? "",
      chapter_13_910_interest_rate: c.chapter_13_910_interest_rate ?? "",
      chapter_13_910_months: c.chapter_13_910_months ?? "",
      chapter_13_910_payment: c.chapter_13_910_payment ?? "",

      // Lien strip
      chapter_13_lien_secured_amount: c.chapter_13_lien_secured_amount ?? "",
      chapter_13_lien_other_lien_amount: c.chapter_13_lien_other_lien_amount ?? "",
      chapter_13_lien_interest_rate: c.chapter_13_lien_interest_rate ?? "",
      chapter_13_lien_months: c.chapter_13_lien_months ?? "",
      chapter_13_lien_payment: c.chapter_13_lien_payment ?? "",

      // Third
      chapter_13_third_party: c.chapter_13_third_party ?? "",
    });
    setShowAddPayment(false);
    setShowCh13Options(hasCh13Data(c));
  };
  
  const closeEditModal = () => {
    setEditingCreditor(null);
    setEditForm({});
  };
  
  const handleUpdateCreditor = async (e) => {
    e.preventDefault();
    setEditSaving(true);

    try {
      // Normalize payload types so we don't send '' to number fields
      const payload = {
        ...editForm,
        debt_type: lc(editForm.debt_type),
        account_type: lc(editForm.account_type),
        chapter_13_secured_treatment: lc(editForm.chapter_13_secured_treatment),

        // Common numeric fields
        balance: nOrNull(editForm.balance),
        arrearage: nOrNull(editForm.arrearage),
        priority_amount: nOrNull(editForm.priority_amount),

        // Maintenance
        chapter_13_maintenance_payment_amount: nOrNull(editForm.chapter_13_maintenance_payment_amount),
        chapter_13_maintenance_arrearage_payment: nOrNull(editForm.chapter_13_maintenance_arrearage_payment),
        chapter_13_maintenance_arrearage_months: iOrNull(editForm.chapter_13_maintenance_arrearage_months),
        chapter_13_maintenance_arrearage_interest_rate: nOrNull(editForm.chapter_13_maintenance_arrearage_interest_rate),

        // Cramdown
        chapter_13_cramdown_secured_amount: nOrNull(editForm.chapter_13_cramdown_secured_amount),
        chapter_13_cramdown_interest_rate: nOrNull(editForm.chapter_13_cramdown_interest_rate),
        chapter_13_cramdown_months: iOrNull(editForm.chapter_13_cramdown_months),
        chapter_13_cramdown_payment: nOrNull(editForm.chapter_13_cramdown_payment),

        // 910
        chapter_13_910_secured_amount: nOrNull(editForm.chapter_13_910_secured_amount),
        chapter_13_910_interest_rate: nOrNull(editForm.chapter_13_910_interest_rate),
        chapter_13_910_months: iOrNull(editForm.chapter_13_910_months),
        chapter_13_910_payment: nOrNull(editForm.chapter_13_910_payment),

        // Lien strip
        chapter_13_lien_secured_amount: nOrNull(editForm.chapter_13_lien_secured_amount),
        chapter_13_lien_other_lien_amount: nOrNull(editForm.chapter_13_lien_other_lien_amount),
        chapter_13_lien_interest_rate: nOrNull(editForm.chapter_13_lien_interest_rate),
        chapter_13_lien_months: iOrNull(editForm.chapter_13_lien_months),
        chapter_13_lien_payment: nOrNull(editForm.chapter_13_lien_payment),
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${editingCreditor.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        throw new Error(`Server responded with ${res.status}`);
      }

      const updated = await res.json();
      // update list immediately
      setCreditors(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      // also keep the local editing object in sync (if user continues editing)
      setEditingCreditor(updated);
      // refresh list from backend to ensure server-calculated fields are reflected
      fetchCreditors();
      closeEditModal();
    } catch (err) {
      alert('Creditor update failed: ' + err.message);
    } finally {
      setEditSaving(false);
    }
  };
  const cache = useRef({});
  const normalizeZip = z => (z || "").split("-")[0].trim();
  const toggleCommon = (c) => {
    const streetVal = c.street || c.street1 || "";
    const lookupKey = [
      normalizeText(c.account_name),
      normalizeText(streetVal),
      normalizeText(c.street2),
      normalizeText(c.city),
      normalizeText(c.state),
      normalizeZip(c.zip_code),
    ].join("|");
    
    const commonId = commonMap[lookupKey];
    if (commonId) {
      removeFromCommon(commonId);
    } else {
      addToCommon(c);
    }
  };
  const fetchSuggestions = useCallback(
    
    debounce(async ({ value }) => {
      if (value.length < 3) {
        setNameSuggestions([]);
        return;
      }
      if (cache.current[value]) {
        return setNameSuggestions(cache.current[value]);
      }
      const token = localStorage.getItem("accessToken");
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/common-creditors/?q=${encodeURIComponent(value)}`,
        { headers: { Authorization:`Bearer ${token}` } }
      );
      if (resp.ok) {
        setNameSuggestions(await resp.json());
      }
    }, 600),
    []
  );
  useEffect(() => {
    (async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/common-creditors-list/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCommonCreditors(await res.json());
      }
    })();
  }, [token]);

    const onSuggestionsFetchRequested = ({ value }) => {
        fetchSuggestions({ value });
    };

    const onSuggestionsClearRequested = () => {
        setNameSuggestions([]);
    };

    // when the user types in the input:
    const onNameChange = (event, { newValue }) => {
        setNewCreditor({ ...newCreditor, account_name: newValue });
    };
    const commonMap = useMemo(() => {
      const m = {};
      commonCreditors.forEach(cc => {
        const key = [
          normalizeText(cc.account_name),
          normalizeText(cc.street1),
          normalizeText(cc.street2),
          normalizeText(cc.city),
          normalizeText(cc.state),
          normalizeZip(cc.zip_code),
        ].join("|");
        m[key] = cc.id;
      });
      return m;
    }, [commonCreditors]);
    // when they pick one suggestion:
    const onSuggestionSelected = (event, { suggestion }) => {
        setNewCreditor({
        ...newCreditor,
        account_name: suggestion.account_name,
        street:        suggestion.street1,
        street2:        suggestion.street2,
        city:          suggestion.city,
        state:         suggestion.state,
        zip_code:      suggestion.zip_code,
        account_type:  suggestion.account_type,
        use_common_address: true,
        });
    };

    const getSuggestionValue = s => s.account_name;
    const renderSuggestion   = s => <div>{s.account_name}</div>;
    const addToCommon = async (c) => {
      const payload = {
        account_name: c.account_name,
        street1:      c.street1,
        street2:      c.street2,
        city:         c.city,
        state:        c.state,
        zip_code:     c.zip_code,
        account_type: c.account_type,
      };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/common-creditors-list/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const newCc = await res.json();
        setCommonCreditors(ccs => [...ccs, newCc]);
      }
    };
  
    const removeFromCommon = async (commonId) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/common-creditors/${commonId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCommonCreditors(ccs => ccs.filter(x => x.id !== commonId));
      }
    };
  const [newCreditor, setNewCreditor] = useState({
    account_name: "",
    balance: "",
    street: "",
    street2: "",
    zip_code: "",
    debt_type: "",
    account_type: "",
    date_opened: "",
    account_number: "",
    lien_book: "",
    lien_page: "",
    lien_case_number: "",
    lien_county: "",
    who_owes: "",
    use_common_address: false,
  });
  const [newVehicle, setNewVehicle] = useState({
    year: "",
    make: "",
    model: "",
    mileage:"",
    ownership: "debtor1"
  });
  
  const [newProperty, setNewProperty] = useState({
    street1: "",
    city: "",
    state: "",
    zip_code: "",
    ownership: "debtor1"
  });
  useEffect(() => {
    async function fetchCase() {
      try {
        const res = await fetch(`${API_URL}/case/${caseId}/overview/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load case');
        setCaseData(await res.json());
      } catch (e) {
        console.error(e);
      }
    }
    fetchCase();
  }, [caseId]);
  useEffect(() => {
    if (showAddModal || showGovernmentCreditorModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  
    // Clean up on component unmount just in case
    return () => {
      document.body.style.overflow = '';
    };
  }, [showAddModal, showGovernmentCreditorModal]);
  useEffect(() => {
    const fetchCase = async () => {
      try {
        const token = localStorage.getItem("accessToken");// or pass it via props
        const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/case-details/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCaseData(prev => ({
            ...prev,
            ...data,
            real_properties: data.real_estates,
            vehicles: data.vehicles
          }));
      } catch (err) {
        console.error("Error fetching case:", err);
      } finally {
        setCaseLoading(false);
      }
    };
  
    fetchCase();
  }, []);

  useEffect(() => {
    const fetchLinkableAssets = async () => {
      setLinkableAssetsLoading(true);
      setLinkableAssetsError("");
      try {
        const res = await fetch(
          `${API_URL}/case/${caseId}/creditors/linkable-assets/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error("Failed to load the case assets");
        const payload = await res.json();
        setLinkableAssets(Array.isArray(payload.assets) ? payload.assets : []);
      } catch (error) {
        console.error("Error fetching linkable assets:", error);
        setLinkableAssetsError(error.message || "Failed to load the case assets");
      } finally {
        setLinkableAssetsLoading(false);
      }
    };

    if (caseId) fetchLinkableAssets();
  }, [caseId, token]);

  const fetchCreditors = async () => {
    //console.log("Using BACKEND_URL:", BACKEND_URL);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setCreditors(data);
      console.log("commonCreditors payload:", commonCreditors)
    } else {
      console.error("Failed to fetch creditors");
    }
  };
  
  // Then useEffect just calls it
  useEffect(() => {
    fetchCreditors();
  }, []);
  useEffect(() => {
    const fetchCreditors = async () => {
    
      const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setCreditors(data);
      } else {
        console.error("Failed to fetch creditors");
      }
      setLoading(false);
    };

    fetchCreditors();

  }, [caseId, token]);
  const debtor1 = primaryDebtor(caseData?.debtors, { filingOnly: true });
  const debtor2 = secondaryFilingDebtor(caseData?.debtors);
  const debtor1Name = debtor1?.first_name || "Debtor 1";
  const debtor2Name = debtor2?.first_name || "Spouse";
  const selectedAccountType = lc(selectedCreditor?.account_type);
  const realPropertyAccountTypes = ["mortgage", "judgment lien", "statutory lien"];
  const otherLinkableAssets = useMemo(
    () => linkableAssets.filter(
      (asset) => !["vehicle", "realproperty"].includes(lc(asset.asset_type)),
    ),
    [linkableAssets],
  );
  const otherAssetGroups = useMemo(
    () => otherLinkableAssets.reduce((groups, asset) => {
      const label = asset.asset_type_label || "Other Property";
      if (!groups[label]) groups[label] = [];
      groups[label].push(asset);
      return groups;
    }, {}),
    [otherLinkableAssets],
  );

  const describeLinkedAsset = (link) => {
    if (link?.asset_label) return link.asset_label;
    const linked = linkableAssets.find((asset) => (
      lc(asset.asset_type) === lc(link?.asset_type)
      && String(asset.object_id) === String(link?.object_id)
    ));
    if (linked) return linked.label;
    if (lc(link?.asset_type) === "vehicle") {
      const vehicle = (caseData?.vehicles || []).find(
        (item) => String(item.id) === String(link.object_id),
      );
      if (vehicle) return `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim();
    }
    if (lc(link?.asset_type) === "realproperty") {
      const property = (caseData?.real_properties || []).find(
        (item) => String(item.id) === String(link.object_id),
      );
      if (property) return [property.street1, property.city].filter(Boolean).join(", ");
    }
    return link?.asset_type_label || "Unknown asset";
  };
  
  useEffect(() => {
    if (!caseData) return;
    const needsLinking = creditors.find(c =>
      lc(c.debt_type) === "secured" &&
      ["mortgage", "auto", "judgment lien", "statutory lien", "other"].includes(lc(c.account_type)) &&
      (!c.asset_links || c.asset_links.length === 0)
    );
    if (needsLinking) {
      setSelectedCreditor(needsLinking);
      setShowLinkAssetModal(true);
    }
  }, [creditors, caseData]);

  useEffect(() => {
    setSelectedAssetId("");
    setSelectedAssetType("");
    setDeclineReason("");
  }, [selectedCreditor?.id]);

  const validateAddressFields = async (addressObj) => {
      let { street, street2, zip, city, state } = addressObj;
      let address1 = street;
      let address2 = street2;
  
      // Fallback if city or state is missing
      if (!city || !state) {
        const result = zipcodes.lookup(zip);
        if (result) {
          city = result.city;
          state = result.state;
        } else {
          city = "afdafdafdaf";
          state = "IL";
        }
      }
  
      try {
        const response = await fetch(`${BACKEND_URL}/validate-address-intake`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address1, address2, zip, city, state }),
        });
  
        if (!response.ok) {
          throw new Error(await response.text());
        }
  
        const validated = await response.json();
        console.log(validated);
  
        // If we have a match, return the standardized address object
        if (validated.matches && validated.matches.length > 0 && validated.address) {
          return validated.address;
        } else {
          alert('Error: USPS could not verify your address.');
          return false;
        }
      } catch (error) {
        console.error('Address validation error:', error);
        setSaving(false);
        alert('Address validation failed: ' + error.message);
        return false;
      }
    };
  
  const getWhoOwesLabel = (code) => {
    if (!debtor1) return code;
  
    switch (code) {
      case "debtor1":
        return debtor1.first_name;
      case "debtor2":
        return debtor2 ? debtor2.first_name : "Spouse";
      case "joint":
        return debtor2 ? `${debtor1.first_name} and ${debtor2.first_name}` : debtor1.first_name;
      case "debtor1_third":
        return `${debtor1.first_name} and a third party`;
      case "debtor2_third":
        return debtor2 ? `${debtor2.first_name} and a third party` : "Spouse and a third party";
      case "authorized":
        return "Authorized User";
      default:
        return code;
    }
  };
    const debtSummary = useMemo(() => summarizeCreditors(creditors), [creditors]);
    const handleChangeAccountType = async (reasonCode) => {
        setUpdatingType(true);
      
        if (!selectedCreditor) return;
      
        try {
          if (reasonCode === "paid") {
            // Delete creditor instead of updating
            await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${selectedCreditor.id}/delete/`, {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
          } else {
            // Downgrade account type
            const downgradeMap = {
              deficiency: "deficiency",
              mistake: "unsecured",
            };
      
            const newType = downgradeMap[reasonCode] || "unsecured";
      
            await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/link/`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                action: "update_type",
                creditor_id: selectedCreditor.id,
                account_type: newType,
              }),
            });
          }
      
          setShowLinkAssetModal(false);
          setSelectedCreditor(null);
          setSelectedAssetId("");
          setSelectedAssetType("");
          setDeclineReason("");
          fetchCreditors();
        } catch (err) {
          alert("Failed to update or remove creditor.");
        } finally {
          setUpdatingType(false);
        }
      };
      const handleAddCreditor = async (e) => {
        e.preventDefault();
        setSaving(true);

        const check_full_address = {
          street: newCreditor.street,
          street2: newCreditor.street2,
          zip: newCreditor.zip_code,
        };


        let updatedCreditor = {};

        if (newCreditor.use_common_address) {
          // Use the common-creditor address exactly, no USPS call
          updatedCreditor = {
            ...newCreditor,
            full_address: `${newCreditor.street}${newCreditor.street2 ? ', ' + newCreditor.street2 : ''}, ${newCreditor.city}, ${newCreditor.state} ${newCreditor.zip_code}`,
          };
        } else {
          const validatedAddr = await validateAddressFields(check_full_address);
          if (validatedAddr) {
            const addr = validatedAddr;
            updatedCreditor = {
              ...newCreditor,
              street: addr.streetAddressAbbreviation || newCreditor.street,
              street2: addr.secondaryAddress,
              city: addr.city || newCreditor.city,
              state: addr.state || newCreditor.state,
              zip_code: addr.ZIPCode || newCreditor.zip_code,
              full_address: `${addr.streetAddressAbbreviation} ${addr.secondaryAddress ? ', ' + addr.secondaryAddress : newCreditor.street2}, ${addr.city}, ${addr.state} ${addr.ZIPCode}`,
            };
          } else {
            updatedCreditor = {
              ...newCreditor,
              street: newCreditor.street,
              street2: newCreditor.street2,
              city: newCreditor.city,
              state: newCreditor.state,
              zip_code: newCreditor.zip_code,
              full_address: `${newCreditor.street}${newCreditor.street2 ? ', ' + newCreditor.street2 : ''}, ${newCreditor.city}, ${newCreditor.state} ${newCreditor.zip_code}`,
            };
          }
        }
        const token = localStorage.getItem("accessToken");

        const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/add-creditor/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedCreditor),
        });

        if (res.ok) {
          const newData = await res.json();
          setCreditors((prev) => [...prev, newData]);
          setShowAddModal(false);
          fetchCreditors();
        } else {
          alert("Failed to add creditor.");
        }

        setSaving(false);
      };

      const governmentCreditorAlreadyAdded = (governmentCreditor) =>
        creditors.some((creditor) => (
          normalizeText(creditor.account_name) === normalizeText(governmentCreditor.account_name)
          && normalizeText(creditor.street1) === normalizeText(governmentCreditor.street)
          && normalizeZip(creditor.zip_code || "") === normalizeZip(governmentCreditor.zip_code)
        ));

      const openGovernmentCreditorModal = () => {
        setSelectedGovernmentCreditorIds([]);
        setGovernmentCreditorStatus("");
        setShowGovernmentCreditorModal(true);
      };

      const handleAddGovernmentCreditors = async () => {
        const selected = GOVERNMENT_CREDITORS.filter(
          (creditor) => selectedGovernmentCreditorIds.includes(creditor.id)
            && !governmentCreditorAlreadyAdded(creditor)
        );
        if (!selected.length) return;

        setAddingGovernmentCreditors(true);
        setGovernmentCreditorStatus("");

        const results = await Promise.all(selected.map(async (creditor) => {
          const fullAddress = [
            creditor.street,
            creditor.street2,
            `${creditor.city}, ${creditor.state} ${creditor.zip_code}`,
          ].filter(Boolean).join(", ");

          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/case/${caseId}/add-creditor/`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  account_name: creditor.account_name,
                  street: creditor.street,
                  street2: creditor.street2,
                  city: creditor.city,
                  state: creditor.state,
                  zip_code: creditor.zip_code,
                  balance: "0",
                  debt_type: "unsecured",
                  account_type: "unsecured",
                  who_owes: "debtor1",
                  additional_notified: true,
                  use_common_address: true,
                  full_address: fullAddress,
                }),
              }
            );

            const payload = await response.json().catch(() => ({}));
            return {
              id: creditor.id,
              ok: response.ok,
              error: payload.error || `Request failed (${response.status})`,
            };
          } catch (error) {
            return {
              id: creditor.id,
              ok: false,
              error: error.message || "Network request failed",
            };
          }
        }));

        const failed = results.filter((result) => !result.ok);
        const addedCount = results.length - failed.length;
        await fetchCreditors().catch((error) => {
          console.error("Could not refresh creditors after adding government creditors:", error);
        });
        setAddingGovernmentCreditors(false);

        if (!failed.length) {
          setShowGovernmentCreditorModal(false);
          setSelectedGovernmentCreditorIds([]);
          setGovernmentCreditorStatus("");
          return;
        }

        setSelectedGovernmentCreditorIds(failed.map((result) => result.id));
        setGovernmentCreditorStatus(
          `${addedCount} added; ${failed.length} could not be added. ${failed[0].error}`
        );
      };

      const handleLinkAsset = async () => {
        if (!selectedCreditor || !selectedAssetId) return;

        const accountType = lc(selectedCreditor.account_type);
        const asset_type = selectedAssetType || (
          accountType === "auto"
            ? "vehicle"
            : ["mortgage", "judgment lien", "statutory lien"].includes(accountType)
              ? "realproperty"
              : ""
        );
        if (!asset_type) {
          alert("Please select an asset.");
          return;
        }
      
        setLinking(true);
        
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/link/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "link",
              creditor_id: selectedCreditor.id,
              asset_type: asset_type,
              object_id: selectedAssetId,
            }),
          });
      
          if (res.ok) {
            // Success: close modal, clear state, refresh if needed
            setShowLinkAssetModal(false);
            setSelectedCreditor(null);
            setSelectedAssetId("");
            setSelectedAssetType("");
      
          } else {
            const data = await res.json().catch(() => ({}));
            alert("Failed to link asset: " + (data.error || `server responded with ${res.status}`));
          }
        } catch (err) {
          console.error("Error linking asset:", err);
          alert("An error occurred.");
        } finally {
            setLinking(false);
            fetchCreditors();
        }
      };

    const deleteCreditor = async (creditorId) => {
        
        const confirm = window.confirm("Are you sure you want to delete this creditor?");
        
        if (!confirm) return;
        setDeletingIds(prev => [...prev, creditorId]);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${creditorId}/delete/`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            });

            if (res.ok) {
            setCreditors((prev) => prev.filter((c) => c.id !== creditorId));
            } else {
            alert("Failed to delete creditor.");
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred.");
        } finally {
        setDeletingIds(prev => prev.filter(x => x !== creditorId));
        }
    };

    const sortedCreditors = useMemo(() => [...creditors].sort((a, b) => {
        let result = 0;
      
        if (sortOption === "name") {
          const nameA = a?.account_name || "";
          const nameB = b?.account_name || "";
          result = nameA.localeCompare(nameB);
        } else if (sortOption === "date") {
          const dateA = a?.date_opened ? new Date(a.date_opened) : new Date(0);
          const dateB = b?.date_opened ? new Date(b.date_opened) : new Date(0);
          result = dateA - dateB;
        } else if (sortOption === "balance") {
          const balanceA = Number(a?.balance || 0);
          const balanceB = Number(b?.balance || 0);
          result = balanceA - balanceB;
        }
      
        return sortAsc ? result : -result;
      }), [creditors, sortOption, sortAsc]);

      const visibleCreditors = useMemo(
        () => (responsibilityFilter === 'all'
          ? sortedCreditors
          : sortedCreditors.filter(
            (creditor) => responsibilityGroup(creditor) === responsibilityFilter,
          )),
        [sortedCreditors, responsibilityFilter],
      );

      const groupedByType = useMemo(() => {
  const groups = {
    secured:   { title: 'Secured',    key: 'secured',    items: [], total: 0 },
    priority:  { title: 'Priority',   key: 'priority',   items: [], total: 0 },
    unsecured: { title: 'Unsecured',  key: 'unsecured',  items: [], total: 0 },
    studentLoans: { title: 'Student Loans', key: 'student-loans', items: [], total: 0 },
    otherUnsecured: { title: 'Other Unsecured', key: 'other-unsecured', items: [], total: 0 },
    executory: { title: 'Executory',  key: 'executory',  items: [], total: 0 },
  };
  (visibleCreditors || []).forEach(c => {
    const k = (c.debt_type || 'unsecured').toLowerCase();
    if (k === 'unsecured' && splitUnsecured) {
      const unsecuredBucket = isStudentLoanCreditor(c)
        ? groups.studentLoans
        : groups.otherUnsecured;
      unsecuredBucket.items.push(c);
      unsecuredBucket.total += Number(c.balance || 0);
      return;
    }
    const bucket = groups[k] || groups.unsecured;
    bucket.items.push(c);
    bucket.total += Number(c.balance || 0);
  });
  return splitUnsecured
    ? [groups.secured, groups.priority, groups.studentLoans, groups.otherUnsecured, groups.executory]
    : [groups.secured, groups.priority, groups.unsecured, groups.executory];
}, [visibleCreditors, splitUnsecured]);

const renderGroupedRow = (c) => (
  <div key={c.id} className="flex items-center justify-between p-2 border-b text-sm">
    <div className="min-w-0">
      <div className="font-medium truncate">{c.account_name}</div>
      <div className="text-xs text-gray-500 truncate">
        {(c.account_type || '').toString()} • {getWhoOwesLabel(c.who_owes)}
      </div>
    </div>
    <div className="flex items-center gap-3 ml-4">
      <div className="text-right w-28 tabular-nums">
        ${Number(c.balance || 0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
      </div>
      <button
        type="button"
        onClick={() => openEditModal(c)}
        className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
      >
        Edit
      </button>
    </div>
  </div>
);

      const allVisibleIds = visibleCreditors.map(c => c.id);
const allSelected = allVisibleIds.length > 0
  && allVisibleIds.every((id) => selectedIds.includes(id));

const toggleSelect = (id) => {
  setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
};

const clearSelection = () => setSelectedIds([]);

const selectAllVisible = () => setSelectedIds(allVisibleIds);

const handleToggleSelectAll = () => {
  if (allSelected) {
    clearSelection();
  } else {
    selectAllVisible();
  }
};

const bulkDeleteSelected = async () => {
  if (selectedIds.length === 0) return;
  const confirm = window.confirm(`Delete ${selectedIds.length} selected creditor(s)? This cannot be undone.`);
  if (!confirm) return;

  // show spinners on selected
  setDeletingIds(prev => Array.from(new Set([...prev, ...selectedIds])));

  try {
    const token = localStorage.getItem('accessToken');

    const results = await Promise.allSettled(
      selectedIds.map(id =>
        fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/creditors/${id}/delete/`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })
      )
    );

    const failed = results.filter(r => r.status === 'rejected' || (r.value && !r.value.ok));
    if (failed.length > 0) {
      alert(`${failed.length} deletion(s) failed. Others may have succeeded.`);
    }
    const lc = (v) => (v ?? '').toString().trim().toLowerCase();
    const succeededIds = selectedIds.filter((_, idx) => {
      const r = results[idx];
      return r.status === 'fulfilled' && r.value && r.value.ok;
    });

    if (succeededIds.length > 0) {
      setCreditors(prev => prev.filter(c => !succeededIds.includes(c.id)));
    }
  } catch (e) {
    console.error(e);
    alert('Bulk delete encountered an error.');
  } finally {
    setSelectedIds([]);
    setDeletingIds(prev => prev.filter(id => !selectedIds.includes(id)));
  }
};

const amendedDefSelected = selectedSchedules.includes('all')
  || selectedSchedules.includes('D')
  || selectedSchedules.includes('EF');
const recommendedNoticeCandidates = amendedScheduleService.notice_candidates || [];
const otherNoticeCandidates = amendedScheduleService.other_docket_candidates || [];
const noticeChoices = [
  ...recommendedNoticeCandidates,
  ...otherNoticeCandidates.filter(candidate => (
    !recommendedNoticeCandidates.some(recommended => (
      String(recommended.docket_id) === String(candidate.docket_id)
    ))
  )),
];

  if (loading) return <div className="flex flex-col items-center justify-center h-[40vh] text-center text-gray-700 space-y-4">
  <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
  <div>
    <p className="text-lg font-medium">Loading creditors…</p>
    <p className="text-sm text-gray-500 mt-1">Please refresh if they do not appear after 30 seconds.</p>
  </div>
</div>;
  if (!caseData) return <div>Case not found</div>;
  //console.log(commonCreditors)
  return (
    
    <div className="mx-auto w-full px-2 sm:px-4 md:px-6 lg:px-8">
      
      <h1 className="text-2xl font-bold text-indigo-700 mb-4">Your Creditors</h1>
      {showAmendedModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="max-h-[90vh] overflow-y-auto bg-white p-6 rounded shadow max-w-md w-full">
            {amendStep === 1 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Reason for Amendment</h3>
                <textarea
                  value={amendedReason}
                  onChange={e => setAmendedReason(e.target.value)}
                  className="w-full border rounded p-2"
                  rows={4}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">Select Creditors to Serve</h3>
                {amendedDefSelected && (
                  <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <label className="block text-sm font-semibold text-amber-950">
                      Notice of Bankruptcy Case to include with mailed service
                    </label>
                    {amendedScheduleService.load_error ? (
                      <p className="mt-2 text-sm font-medium text-red-700">
                        {amendedScheduleService.load_error}
                      </p>
                    ) : noticeChoices.length > 0 ? (
                      <>
                        <select
                          value={caseFilingNotice?.docket_id || ''}
                          onChange={event => {
                            const selected = noticeChoices.find(
                              item => String(item.docket_id) === event.target.value
                            );
                            setCaseFilingNotice(selected || null);
                            setAmendmentError('');
                          }}
                          className="mt-2 block w-full rounded border border-amber-300 bg-white px-2 py-2 text-sm"
                        >
                          <option value="">Select the notice docket</option>
                          {recommendedNoticeCandidates.length > 0 && (
                            <optgroup label="Recommended notice dockets">
                              {recommendedNoticeCandidates.map(candidate => (
                                <option key={candidate.docket_id} value={candidate.docket_id}>
                                  Docket {candidate.docket_number} - {(candidate.docket_text || '').slice(0, 110)}
                                  {candidate.has_pdf ? '' : ' (PDF not saved)'}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {otherNoticeCandidates.length > 0 && (
                            <optgroup label="Other saved docket PDFs">
                              {otherNoticeCandidates.map(candidate => (
                                <option key={candidate.docket_id} value={candidate.docket_id}>
                                  Docket {candidate.docket_number} - {(candidate.docket_text || '').slice(0, 110)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        {recommendedNoticeCandidates.length === 0 && (
                          <p className="mt-2 text-sm font-medium text-amber-900">
                            The notice was not recognized automatically. Select it from the saved docket PDFs.
                          </p>
                        )}
                        {caseFilingNotice && !caseFilingNotice.has_pdf && (
                          <p className="mt-2 text-sm font-medium text-red-700">
                            This docket has no saved PDF. Retrieve it through Fetch Dockets before continuing.
                          </p>
                        )}
                        <p className="mt-2 text-xs text-amber-900">
                          The filed amendment will cite this docket. A separate mailed-service copy will contain the filing debtors’ full Social Security numbers and the selected court notice.
                        </p>
                      </>
                    ) : (
                      <p className="mt-2 text-sm font-medium text-red-700">
                        No saved docket PDFs were found. Retrieve the Notice of Bankruptcy Case through Fetch Dockets first.
                      </p>
                    )}
                  </div>
                )}
                <MailingMatrixServiceSelector
                  caseId={caseId}
                  value={mailingMatrixService}
                  onChange={service => {
                    setMailingMatrixService(service);
                    if (!service?.enabled) return;
                    const matrixMethod = service.method || 'USM';
                    const matrixRecipients = service.recipients || [];
                    setAmendedCreditors(prev => prev.filter(selected => {
                      const alreadyOnMatrix = matrixRecipients.some(matrixRecipient => (
                        matrixRecipient.name === selected.name
                        && matrixRecipient.street1 === selected.street1
                        && matrixRecipient.zip_code === selected.zip_code
                      ));
                      return !alreadyOnMatrix || selected.method !== matrixMethod;
                    }));
                  }}
                />
                {mailingMatrixService?.enabled && (
                  <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                    The matrix is the base list. Use the choices below only for additional creditors or a different service method.
                  </p>
                )}
                {!mailingMatrixService?.enabled && (
                  <button
                    onClick={() =>
                      setAmendedCreditors(creditors2.map(c => ({ ...c, method: 'USM' })))
                    }
                    className="text-sm text-blue-600 mb-2"
                  >
                    Select All as USM
                  </button>
                )}
                <button
                  onClick={() => setAmendedCreditors([])}
                  className="ml-4 text-sm text-red-600 mb-2"
                >
                  {mailingMatrixService?.enabled ? 'Clear Additions / Overrides' : 'Remove All'}
                </button>
                <div className="mb-2 text-sm font-semibold text-gray-800">
                  {mailingMatrixService?.enabled
                    ? 'Additional creditors or service-method overrides'
                    : 'Individual creditors to serve'}
                </div>
                <div className="max-h-64 overflow-y-auto mb-4 border">
                  {creditors2.map((c, idx) => {
                    const selected = amendedCreditors.find(ac => (
                      ac.name === c.name
                      && ac.street1 === c.street1
                      && ac.zip_code === c.zip_code
                    ));
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 border-b">
                        <div className="flex-1">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-gray-500">
                            {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                          </div>
                        </div>
                        <select
                          value={selected?.method || ''}
                          onChange={e => {
                            const method = e.target.value;
                            setAmendedCreditors(prev => {
                              const matches = ac => (
                                ac.name === c.name
                                && ac.street1 === c.street1
                                && ac.zip_code === c.zip_code
                              );
                              if (!method) {
                                return prev.filter(ac => !matches(ac));
                              }
                              const exists = prev.find(matches);
                              if (exists) {
                                return prev.map(ac =>
                                  matches(ac) ? { ...ac, method } : ac
                                );
                              }
                              return [...prev, { ...c, method }];
                            });
                          }}
                          className="border rounded px-2 py-1"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                {amendmentError && (
                  <p className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
                    {amendmentError}
                  </p>
                )}
                <div className="flex justify-between">
                  <button
                    onClick={() => setAmendStep(1)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (amendedDefSelected) {
                        if (!caseFilingNotice) {
                          setAmendmentError('Select the Notice of Bankruptcy Case docket.');
                          return;
                        }
                        if (!caseFilingNotice.has_pdf) {
                          setAmendmentError('The selected notice must have a saved PDF before the service copy can be generated.');
                          return;
                        }
                        const missingSsn = (amendedScheduleService.filing_debtors || [])
                          .filter(debtor => !String(debtor.ssn || '').trim());
                        if (missingSsn.length > 0) {
                          setAmendmentError(`Save the full Social Security number for ${missingSsn.map(item => item.name).join(', ')} before continuing.`);
                          return;
                        }
                      }
                      setShowAmendedModal(false);
                      setAmendmentError('');
                      setData(prev => ({
                        ...(prev || {}),
                        amendedReason,
                        amendedCreditors,
                        mailingMatrixService,
                        amendedScheduleService,
                        caseFilingNotice,
                      }));
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <h1 className="text-2xl font-semibold">Schedules D, E & F</h1>
      {/* Schedule selection & Amended toggle */}
        <div className="flex flex-wrap items-center space-x-4 mb-4">
          {['D','EF','G','SOI','H','Matrix','all'].map(type => {
            const isAll = type === 'all';
            const isSel = selectedSchedules.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setSelectedSchedules(prev => {
                    // clicking “All” resets to just ['all']
                    if (isAll) return ['all'];
                    // otherwise remove 'all'
                    const withoutAll = prev.filter(s => s !== 'all');
                    // toggle this type
                    if (withoutAll.includes(type)) {
                      return withoutAll.filter(s => s !== type);
                    } else {
                      return [...withoutAll, type];
                    }
                  });
                }}
                className={`px-4 py-2 rounded ${
                  isSel ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {isAll ? 'All' : `Schedule ${type}`}
              </button>
            );
          })}
 </div>
      <div>
            <button
        onClick={() => handleGenerate()}
        disabled={loading}
        className={`mt-4 px-4 py-2 rounded ${loading ? 'bg-gray-400' : 'bg-green-600 text-white'}`}
      >
        Generate Schedules PDF
      </button>
      </div>
      {amendmentError && !showAmendedModal && (
        <p className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
          {amendmentError}
        </p>
      )}
              <div className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={data?.amended ?? false}
            onChange={e => {
              const checked = e.target.checked;
              setData(prev => ({ ...(prev || {}), amended: checked }));
              if (checked) {
                setAmendmentError('');
                setShowAmendedModal(true);
                setAmendStep(1);
              } else {
                setMailingMatrixService(null);
                setCaseFilingNotice(null);
              }
            }}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">Amended Filing</label>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-700 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-gray-900">Debt Summary</p>
            <p className="text-xs text-gray-500">Balances currently listed for this case</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={splitUnsecured}
            onClick={() => setSplitUnsecured((current) => !current)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              splitUnsecured
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-400'
            }`}
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${splitUnsecured ? 'bg-white' : 'bg-gray-300'}`}
            />
            {splitUnsecured ? 'Student loan split on' : 'Split student loans'}
          </button>
        </div>
        <div className={`grid grid-cols-2 gap-4 text-center ${splitUnsecured ? 'lg:grid-cols-5' : 'sm:grid-cols-4'}`}>
            <div>
            <Home className="mx-auto h-5 w-5 text-indigo-600 mb-1" />
            <p className="font-semibold text-gray-900">Secured</p>
            <p>{debtSummary.secured.count} account(s)</p>
            <p>${formatSummaryMoney(debtSummary.secured.amount)}</p>
            </div>
            <div>
            <Gavel className="mx-auto h-5 w-5 text-red-500 mb-1" />
            <p className="font-semibold text-gray-900">Priority</p>
            <p>{debtSummary.priority.count} account(s)</p>
            <p>${formatSummaryMoney(debtSummary.priority.amount)}</p>
            </div>
            {splitUnsecured ? (
              <>
                <div>
                  <GraduationCap className="mx-auto h-5 w-5 text-violet-600 mb-1" />
                  <p className="font-semibold text-gray-900">Student Loans</p>
                  <p>{debtSummary.unsecuredStudentLoans.count} account(s)</p>
                  <p>${formatSummaryMoney(debtSummary.unsecuredStudentLoans.amount)}</p>
                </div>
                <div>
                  <CreditCard className="mx-auto h-5 w-5 text-green-600 mb-1" />
                  <p className="font-semibold text-gray-900">Other Unsecured</p>
                  <p>{debtSummary.unsecuredOther.count} account(s)</p>
                  <p>${formatSummaryMoney(debtSummary.unsecuredOther.amount)}</p>
                </div>
              </>
            ) : (
              <div>
                <CreditCard className="mx-auto h-5 w-5 text-green-600 mb-1" />
                <p className="font-semibold text-gray-900">Unsecured</p>
                <p>{debtSummary.unsecured.count} account(s)</p>
                <p>${formatSummaryMoney(debtSummary.unsecured.amount)}</p>
              </div>
            )}
            <div>
            <Sigma className="mx-auto h-5 w-5 text-gray-600 mb-1" />
            <p className="font-semibold text-gray-900">Total</p>
            <p>{debtSummary.total.count} account(s)</p>
            <p>${formatSummaryMoney(debtSummary.total.amount)}</p>
            </div>
        </div>
        </div>
        {caseData?.joint && debtor2 && (
          <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-4 text-sm text-gray-700 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <div>
                <p className="font-semibold text-gray-900">Debt by Responsibility</p>
                <p className="text-xs text-gray-500">Includes secured, priority, unsecured, and executory debts</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 text-center sm:grid-cols-3">
              {[
                ['debtor1', `${debtor1Name} (Debtor 1)`],
                ['debtor2', `${debtor2Name} (Debtor 2)`],
                ['joint', 'Joint'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setResponsibilityFilter(
                      responsibilityFilter === key ? 'all' : key,
                    );
                    setSelectedIds([]);
                  }}
                  className={`rounded-lg border p-3 transition ${
                    responsibilityFilter === key
                      ? 'border-indigo-500 bg-white ring-2 ring-indigo-200'
                      : 'border-indigo-100 bg-white/80 hover:border-indigo-300'
                  }`}
                  aria-pressed={responsibilityFilter === key}
                >
                  <p className="font-semibold text-gray-900">{label}</p>
                  <p>{debtSummary.responsibility[key].count} account(s)</p>
                  <p className="font-medium tabular-nums">
                    ${formatSummaryMoney(debtSummary.responsibility[key].amount)}
                  </p>
                  <p className="mt-1 text-xs text-indigo-600">
                    {responsibilityFilter === key ? 'Showing these debts' : 'Click to show'}
                  </p>
                </button>
              ))}
            </div>
            {debtSummary.responsibility.other.count > 0 && (
              <p className="mt-3 text-center text-xs text-gray-500">
                {debtSummary.responsibility.other.count} authorized/other account(s) totaling ${formatSummaryMoney(debtSummary.responsibility.other.amount)} are included only in the overall total.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-3 my-6">
  <button
    onClick={() => setShowAddModal(true)}
    className="bg-indigo-600 text-white px-6 py-2 rounded-lg shadow hover:bg-indigo-700 transition"
  >
    <Plus className="w-4 h-4 inline-block mr-2" />
    Add Creditor
  </button>
  <button
    type="button"
    onClick={openGovernmentCreditorModal}
    className="bg-slate-700 text-white px-6 py-2 rounded-lg shadow hover:bg-slate-800 transition"
  >
    <Landmark className="w-4 h-4 inline-block mr-2" />
    Government Creditors
  </button>
</div>
<div className="flex flex-wrap items-center justify-between gap-3 mb-3">
  <h3 className="text-sm font-semibold text-gray-800">Creditors</h3>
  <div className="flex flex-wrap items-center gap-3">
    {caseData?.joint && debtor2 && responsibilityFilter !== 'all' && (
      <button
        type="button"
        onClick={() => {
          setResponsibilityFilter('all');
          setSelectedIds([]);
        }}
        className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-200"
      >
        Showing {responsibilityFilter === 'joint' ? 'Joint' : getWhoOwesLabel(responsibilityFilter)} debts ×
      </button>
    )}
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={groupByType}
        onChange={(e) => setGroupByType(e.target.checked)}
        className="h-4 w-4"
      />
      Group by debt type
    </label>
  </div>
</div>
{groupByType && (
  <div className="space-y-6">
    {groupedByType.map(sec => (
      <div key={sec.key} className="border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <div className="font-semibold">{sec.title}</div>
          <div className="text-sm text-gray-700">
            {sec.items.length} account{sec.items.length === 1 ? '' : 's'} • ${sec.total.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
          </div>
        </div>
        {sec.items.length > 0 ? (
          <div>{sec.items.map(renderGroupedRow)}</div>
        ) : (
          <div className="text-sm text-gray-500 px-3 py-4">No {sec.title.toLowerCase()} creditors</div>
        )}
      </div>
    ))}
  </div>
)}
{showGovernmentCreditorModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
    <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-xl border border-gray-200 flex flex-col">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Add Government Creditors</h2>
          <p className="mt-1 text-sm text-gray-600">
            Select one or more government notice recipients to add to this case.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowGovernmentCreditorModal(false)}
          disabled={addingGovernmentCreditors}
          className="text-2xl leading-none text-gray-500 hover:text-gray-800 disabled:opacity-50"
          aria-label="Close government creditors"
        >
          &times;
        </button>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-3 text-sm">
        <button
          type="button"
          onClick={() => setSelectedGovernmentCreditorIds(
            GOVERNMENT_CREDITORS
              .filter((creditor) => !governmentCreditorAlreadyAdded(creditor))
              .map((creditor) => creditor.id)
          )}
          className="font-medium text-indigo-600 hover:text-indigo-800"
        >
          Select all available
        </button>
        <button
          type="button"
          onClick={() => setSelectedGovernmentCreditorIds([])}
          className="font-medium text-gray-600 hover:text-gray-900"
        >
          Clear selection
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-3">
          {GOVERNMENT_CREDITORS.map((creditor) => {
            const alreadyAdded = governmentCreditorAlreadyAdded(creditor);
            const selected = selectedGovernmentCreditorIds.includes(creditor.id);
            return (
              <label
                key={creditor.id}
                className={`flex items-start gap-3 rounded-lg border p-4 ${
                  alreadyAdded
                    ? 'border-gray-200 bg-gray-50 opacity-65'
                    : selected
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300 cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={alreadyAdded || addingGovernmentCreditors}
                  onChange={(event) => {
                    setSelectedGovernmentCreditorIds((current) => (
                      event.target.checked
                        ? [...current, creditor.id]
                        : current.filter((id) => id !== creditor.id)
                    ));
                  }}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
                    {creditor.account_name}
                    {alreadyAdded && (
                      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                        Already added
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-gray-600">
                    {creditor.street}<br />
                    {creditor.street2}<br />
                    {creditor.city}, {creditor.state} {creditor.zip_code}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="border-t border-gray-200 p-5">
        {governmentCreditorStatus && (
          <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {governmentCreditorStatus}
          </p>
        )}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">
            {selectedGovernmentCreditorIds.length} selected
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowGovernmentCreditorModal(false)}
              disabled={addingGovernmentCreditors}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddGovernmentCreditors}
              disabled={!selectedGovernmentCreditorIds.length || addingGovernmentCreditors}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {addingGovernmentCreditors && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {addingGovernmentCreditors ? 'Adding…' : `Add Selected (${selectedGovernmentCreditorIds.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

{showAddModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
    <div className="bg-white w-full max-w-xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto p-4 sm:p-6 rounded-xl shadow-xl border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Add New Creditor</h2>
        <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-800">
          &times;
        </button>
      </div>
      <form onSubmit={handleAddCreditor} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Creditor Name
              </label>
              <div className="relative">
                <Autosuggest
                    suggestions={nameSuggestions}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={onSuggestionsClearRequested}
                    getSuggestionValue={getSuggestionValue}
                    onSuggestionSelected={onSuggestionSelected}
                    inputProps={{
                    placeholder: 'Creditor Name',
                    value: newCreditor.account_name,
                    onChange: onNameChange,
                    className: 'w-full border rounded px-3 py-1.5'
                    }}
                    renderSuggestion={s => (
                      <li className="px-4 py-2 hover:bg-indigo-50 cursor-pointer">
                        <strong>{s.account_name}</strong>
                        <div className="text-xs text-gray-500 truncate">
                          {[s.street1, s.street2].filter(Boolean).join(", ")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {s.city}, {s.state}
                        </div>
                      </li>
                    )}
                    renderSuggestionsContainer={({ containerProps, children }) => (
                    <ul
                        {...containerProps}
                        className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                    >
                        {children}
                    </ul>
                    )}
                    theme={{
                    suggestionsList: '',
                    suggestion: '',
                    // we’re doing all styling in renderSuggestionsContainer/renderSuggestion
                    }}
                />
                </div>
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Balance</label>
                <input type="number" className="w-full border rounded px-3 py-1.5" value={newCreditor.balance} onChange={(e) => setNewCreditor({...newCreditor, balance: e.target.value})} required />
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Street Address</label>
                <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.street} onChange={(e) => setNewCreditor({...newCreditor, street: e.target.value})} required />
            </div>

            {/* Street Address 2 field (optional) */}
            <div>
              <label className="block font-medium text-gray-700 mb-1">Street Address 2 <span className="text-gray-500 text-sm">(optional)</span></label>
              <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.street2 || ''} onChange={(e) => setNewCreditor({...newCreditor, street2: e.target.value})} />
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Zip Code</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-1.5" value={newCreditor.zip_code} onChange={(e) => setNewCreditor({...newCreditor, zip_code: e.target.value, use_common_address:false})} required />
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Debt Type</label>
                <select className="w-full border rounded px-3 py-1.5" value={newCreditor.debt_type} onChange={(e) => setNewCreditor({...newCreditor, debt_type: lc(e.target.value)})} required>
                <option value="">Select</option>
                <option value="secured">Secured</option>
                <option value="priority">Priority</option>
                <option value="unsecured">Unsecured</option>
                <option value="executory">Executory Contract</option>
                </select>
            </div>

            {newCreditor.debt_type && (
                <div>
                <label className="block font-medium text-gray-700 mb-1">Account Type</label>
                <select className="w-full border rounded px-3 py-1.5" value={newCreditor.account_type} onChange={(e) => setNewCreditor({...newCreditor, account_type: e.target.value})}>
                    <option value="">Select</option>
                    {newCreditor.debt_type === "secured" && <>
                    <option value="mortgage">Mortgage</option>
                    <option value="auto">Auto</option>
                    <option value="judgment lien">Judgment Lien</option>
                    <option value="statutory lien">Statutory Lien</option>
                    <option value="other">Other Secured Loan / Collateral</option>
                    </>}
                    {newCreditor.debt_type === "priority" && <>
                    <option value="tax">Tax</option>
                    <option value="child support">Child Support</option>
                    <option value="intoxicated">Intoxicated</option>
                    </>}
                    {newCreditor.debt_type === "unsecured" && <>
                    <option value="medical">Medical</option>
                    <option value="credit card">Credit Card</option>
                    <option value="charge account">Charge Account</option>
                    <option value="personal loan">Personal Loan</option>
                    <option value="student loan">Student Loan</option>
                    <option value="collection">Collection</option>
                    <option value="deficiency">Deficiency</option>
                    <option value="notice purposes">Notice Purposes</option>
                    <option value="unsecured">Unsecured</option>
                    </>}
                </select>
                </div>
            )}

            <div>
                <label className="block font-medium text-gray-700 mb-1">Who Owes</label>
                <select
                value={newCreditor.who_owes}
                onChange={(e) => setNewCreditor({ ...newCreditor, who_owes: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-1.5"
                required
                >
                <option value="">Select</option>
                <option value="debtor1">{debtor1Name}</option>
                {debtor2 && <option value="debtor2">{debtor2Name}</option>}
                {debtor2 && <option value="joint">{debtor1Name} and {debtor2Name}</option>}
                <option value="debtor1_third">{debtor1Name} and a third party</option>
                {debtor2 && <option value="debtor2_third">{debtor2Name} and a third party</option>}
                <option value="authorized">Authorized User</option>
                </select>
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Date Opened <span className="text-gray-500 text-sm">(optional)</span></label>
                <input type="date" className="w-full border rounded px-3 py-1.5" value={newCreditor.date_opened} onChange={(e) => setNewCreditor({...newCreditor, date_opened: e.target.value})} />
            </div>

            <div>
                <label className="block font-medium text-gray-700 mb-1">Account Number <span className="text-gray-500 text-sm">(optional)</span></label>
                <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.account_number} onChange={(e) => setNewCreditor({...newCreditor, account_number: e.target.value})} />
            </div>

            {lc(newCreditor.debt_type) === "secured" && lc(newCreditor.account_type) === "judgment lien" && (
              <>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Civil Case Number <span className="text-gray-500 text-sm">(optional)</span></label>
                  <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.lien_case_number} onChange={(e) => setNewCreditor({...newCreditor, lien_case_number: e.target.value})} />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Recording County <span className="text-gray-500 text-sm">(optional)</span></label>
                  <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.lien_county} onChange={(e) => setNewCreditor({...newCreditor, lien_county: e.target.value})} />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Judgment Book <span className="text-gray-500 text-sm">(optional)</span></label>
                  <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.lien_book} onChange={(e) => setNewCreditor({...newCreditor, lien_book: e.target.value})} />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Judgment Page <span className="text-gray-500 text-sm">(optional)</span></label>
                  <input type="text" className="w-full border rounded px-3 py-1.5" value={newCreditor.lien_page} onChange={(e) => setNewCreditor({...newCreditor, lien_page: e.target.value})} />
                </div>
              </>
            )}

            <div className="col-span-1 sm:col-span-2 flex justify-end gap-4 mt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="text-gray-600 hover:underline">Cancel</button>
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                    {saving && (
                        <span className="inline-block h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin"></span>
                    )}
                    {saving ? "Saving..." : "Save Creditor"}
                    </button>
            </div>
            </form>
    </div>
    
  </div>
  
)}

{editingCreditor && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
    <div className="bg-white w-full max-w-4xl p-6 rounded-xl shadow-xl border border-gray-200 overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Edit Creditor</h2>
        <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-800">&times;</button>
      </div>

      {/* Form */}
      <form onSubmit={handleUpdateCreditor} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 text-sm">
        {/* ───── Row 1: Name | Acct # | Balance ───── */}
        <div>
          <label className="block font-medium text-gray-700 mb-1">Creditor Name</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.account_name}
            onChange={e => setEditForm({ ...editForm, account_name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Account #</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.account_number}
            onChange={e => setEditForm({ ...editForm, account_number: e.target.value })}
          />
        </div>
                <div>
          <label className="block font-medium text-gray-700 mb-1">Date Opened</label>
          <input
            type="date"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.date_opened}
            onChange={e => setEditForm({ ...editForm, date_opened: e.target.value })}
          />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Balance</label>
          <input
            type="number"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.balance}
            onChange={e => setEditForm({ ...editForm, balance: e.target.value })}
            required
          />
        </div>

        {/* ───── Row 2: Street | City | State + Zip ───── */}
        <div className="col-span-1">
          <label className="block font-medium text-gray-700 mb-1">Street</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.street1}
            onChange={e => setEditForm({ ...editForm, street1: e.target.value })}
            required
          />
        </div>
        {/* Street 2 field (optional) */}
        <div className="col-span-1">
          <label className="block font-medium text-gray-700 mb-1">Street 2 <span className="text-gray-500 text-sm">(optional)</span></label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.street2 || ''}
            onChange={e => setEditForm({ ...editForm, street2: e.target.value })}
          />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">City</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.city}
            onChange={e => setEditForm({ ...editForm, city: e.target.value })}
            required
          />
        </div>

          <div className="flex-1">
            <label className="block font-medium text-gray-700 mb-1">State</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-1.5"
              value={editForm.state}
              onChange={e => setEditForm({ ...editForm, state: e.target.value })}
              required
            />
          </div>
          <div className="flex-1">
            <label className="block font-medium text-gray-700 mb-1">Zip</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-1.5"
              value={editForm.zip_code}
              onChange={e => setEditForm({ ...editForm, zip_code: e.target.value })}
              required
            />
          </div>

        
        {/* ───── Row 3: Debt Type | Account Type | Who Owes ───── */}
        <div>
          <label className="block font-medium text-gray-700 mb-1">Debt Type</label>
          <select
            className="w-full border rounded px-3 py-1.5"
            value={editForm.debt_type}
            onChange={e => setEditForm({ ...editForm, debt_type: lc(e.target.value), account_type: "" })}
            required
          >
            <option value="">Select…</option>
            <option value="secured">Secured</option>
            <option value="priority">Priority</option>
            <option value="unsecured">Unsecured</option>
            <option value="executory">Executory Contract</option>
          </select>
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Account Type</label>
          <select
            className="w-full border rounded px-3 py-1.5"
            value={lc(editForm.account_type)}
            onChange={e => setEditForm({ ...editForm, account_type: lc(e.target.value) })}
            disabled={!lc(editForm.debt_type)}
            required
          >
            <option value="">Select…</option>
            {lc(editForm.debt_type) === "secured" && (
              <>
                <option value="mortgage">Mortgage</option>
                <option value="auto">Auto</option>
                <option value="judgment lien">Judgment Lien</option>
                <option value="statutory lien">Statutory Lien</option>
                <option value="other">Other Secured Loan / Collateral</option>
              </>
            )}
            {lc(editForm.debt_type) === "priority" && (
              <>
                <option value="tax">Tax</option>
                <option value="child support">Child Support</option>
                <option value="intoxicated">Intoxicated</option>
              </>
            )}
            {lc(editForm.debt_type) === "unsecured" && (
              <>
                <option value="medical">Medical</option>
                <option value="credit card">Credit Card</option>
                <option value="charge account">Charge Account</option>
                <option value="personal loan">Personal Loan</option>
                <option value="student loan">Student Loan</option>
                <option value="collection">Collection</option>
                <option value="deficiency">Deficiency</option>
                <option value="notice purposes">Notice Purposes</option>
                <option value="unsecured">Unsecured</option>
              </>
            )}
            {lc(editForm.debt_type) === "executory" && (
              <option value="executory">Executory Contract</option>
            )}
          </select>
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Who Owes</label>
          <select
            className="w-full border rounded px-3 py-1.5"
            value={editForm.who_owes}
            onChange={e => setEditForm({ ...editForm, who_owes: lc(e.target.value) })}
            required
          >
            <option value="debtor1">Debtor 1</option>
            <option value="debtor2">Debtor 2</option>
            <option value="joint">Joint</option>
            <option value="debtor1_third">D1 & Third</option>
            <option value="debtor2_third">D2 & Third</option>
            <option value="authorized">Authorized User</option>
          </select>
        </div>

        {/* ───── Secured-only: Nature of Lien + Other ───── */}
        {editForm.debt_type === "secured" && (
          <>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Nature of Lien</label>
              <select
                className="w-full border rounded px-3 py-1.5"
                value={editForm.nature_of_lien}
                onChange={e => setEditForm({ ...editForm, nature_of_lien: lc(e.target.value) })}
              >
                <option value="agreement">Agreement</option>
                <option value="statutory">Statutory</option>
                <option value="judgment">Judgment</option>
                <option value="other">Other</option>
              </select>
            </div>
            {lc(editForm.nature_of_lien) === "other" && (
              <div className="col-span-3">
                <label className="block font-medium text-gray-700 mb-1">Lien Description</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-1.5"
                  value={editForm.lien_description}
                  onChange={e => setEditForm({ ...editForm, lien_description: e.target.value })}
                />
              </div>
            )}

            {(lc(editForm.account_type) === "judgment lien" || lc(editForm.nature_of_lien) === "judgment") && (
              <>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Civil Case Number</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-1.5"
                    value={editForm.lien_case_number || ""}
                    onChange={e => setEditForm({ ...editForm, lien_case_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Recording County</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-1.5"
                    value={editForm.lien_county || ""}
                    onChange={e => setEditForm({ ...editForm, lien_county: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Judgment Book</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-1.5"
                    value={editForm.lien_book || ""}
                    onChange={e => setEditForm({ ...editForm, lien_book: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Judgment Page</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-1.5"
                    value={editForm.lien_page || ""}
                    onChange={e => setEditForm({ ...editForm, lien_page: e.target.value })}
                  />
                </div>
              </>
            )}

         

            {/* ───── Chapter 7 only: Statement of Intent ───── */}
            {caseData?.chapter === "7" && (
              <>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">SOI</label>
                  <select
                    className="w-full border rounded px-3 py-1.5"
                    value={editForm.statement_of_intent_secured_treatment}
                    onChange={e => setEditForm({
                      ...editForm,
                      statement_of_intent_secured_treatment: e.target.value
                    })}
                  >
                    <option value="">Select…</option>
                    <option value="surrender">Surrender</option>
                    <option value="redeem">Retain</option>
                    <option value="reaffirm">Reaffirm</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {editForm.statement_of_intent_secured_treatment === "other" && (
                  <div className="col-span-2">
                    <label className="block font-medium text-gray-700 mb-1">Other Intent Description</label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-1.5"
                      value={editForm.statement_of_intent_other_description}
                      onChange={e => setEditForm({
                        ...editForm,
                        statement_of_intent_other_description: e.target.value
                      })}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ───── Priority-only: Priority Amount ───── */}
        {editForm.debt_type === "priority" && (
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-gray-700 mb-1">Priority Amount</label>
              <input
                type="number"
                step="0.01"
                className="w-full border rounded px-3 py-1.5"
                value={editForm.priority_amount || ""}
                onChange={e => setEditForm({ ...editForm, priority_amount: e.target.value })}
              />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Priority Type</label>
              <select
                className="w-full border rounded px-3 py-1.5"
                value={editForm.priority_type}
                onChange={e => setEditForm({
                  ...editForm,
                  priority_type: e.target.value
                })}
              >
                <option value="">Select…</option>
                <option value="tax">Tax</option>
                <option value="dso">DSO</option>
                <option value="intoxicated">Intoxicated</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        {/* ───── Executory-only: Lease Description ───── */}
        {editForm.debt_type === "executory" && (
          <>
            <div className="col-span-2">
              <label className="block font-medium text-gray-700 mb-1">
                Description of Lease
              </label>
              <input
                type="text"
                className="w-full border rounded px-3 py-1.5"
                value={editForm.description_of_lease || ""}
                onChange={e =>
                  setEditForm({ ...editForm, description_of_lease: e.target.value })
                }
              />
            </div>
            {caseData?.chapter === "7" && (
            <div className="col-span-1">
              <label className="block font-medium text-gray-700 mb-1">
                SoI Assumption
              </label>
              <select
                className="w-full border rounded px-3 py-1.5"
                value={editForm.statement_of_intent_assumption || ""}
                onChange={e =>
                  setEditForm({ ...editForm, statement_of_intent_assumption: e.target.value })
                }
              >
                <option value="">Select...</option>
                <option value="true">Assume</option>
                <option value="false">Reject</option>
              </select>
            </div>
            )}
          </>
        )}

        {/* ───── Row: Recent Payments + Co-Debtor ───── */}
        <div className="col-span-3 flex space-x-4 pt-4">
          <button
            type="button"
            onClick={() => openPaymentsModal(editingCreditor)}
            className="text-indigo-600 hover:underline text-sm"
          >
            View / Edit Payments
          </button>
          <button
            type="button"
            onClick={() => setShowCodebtorModal(true)}
            className="text-indigo-600 hover:underline text-sm"
          >
            Add/Edit Co-Debtor
          </button>
        </div>

        {/* ───── Advanced Options Toggle ───── */}
        <div className="col-span-3">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className="text-gray-600 hover:underline text-sm"
          >
            {showAdvancedOptions ? "Hide" : "Show"} Advanced Options
          </button>
        </div>
        {/* Chapter 13 Plan Treatment Accordion State */}

        {/* ───── Advanced Options Panel ───── */}
        {showAdvancedOptions && (
          <div className="col-span-3 grid grid-cols-2 gap-4 border-t pt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.community_debt}
                onChange={e => setEditForm({ ...editForm, community_debt: e.target.checked })}
              />
              Community Debt
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.subject_to_offset}
                onChange={e => setEditForm({ ...editForm, subject_to_offset: e.target.checked })}
              />
              Subject to Offset
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.additional_notified}
                onChange={e => setEditForm({ ...editForm, additional_notified: e.target.checked })}
              />
              Additional Notified
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editForm.fdic_bank}
                onChange={e => setEditForm({ ...editForm, fdic_bank: e.target.checked })}
              />
              FDIC Bank
            </label>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Dispute Status</label>
              <select
                className="w-full border rounded px-3 py-1.5"
                value={editForm.dispute_status}
                onChange={e => setEditForm({ ...editForm, dispute_status: e.target.value })}
              >
                <option value="none">None</option>
                <option value="contingent">Contingent</option>
                <option value="unliquidated">Unliquidated</option>
                <option value="disputed">Disputed</option>
              </select>
            </div>
            {/* …you can similarly add melissa_updated, authorized_user, priority fields, executory fields… */}
          </div>
        )}
        
{showCodebtorModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
    <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl border border-gray-200">
      <h3 className="text-lg font-bold mb-4">Co-Debtor Details</h3>
      <div className="grid grid-cols-1 gap-4 text-sm">
        <div>
          <label className="block font-medium text-gray-700 mb-1">Co-Debtor Name</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.codebtor}
            onChange={e => setEditForm({ ...editForm, codebtor: e.target.value })}
          />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Street 1</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.codebtor_street1}
            onChange={e => setEditForm({ ...editForm, codebtor_street1: e.target.value })}
          />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Street 2</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.codebtor_street2}
            onChange={e => setEditForm({ ...editForm, codebtor_street2: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-1.5"
              value={editForm.codebtor_city}
              onChange={e => setEditForm({ ...editForm, codebtor_city: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-1">State</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-1.5"
              value={editForm.codebtor_state}
              onChange={e => setEditForm({ ...editForm, codebtor_state: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Zip Code</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5"
            value={editForm.codebtor_zip_code}
            onChange={e => setEditForm({ ...editForm, codebtor_zip_code: e.target.value })}
          />
        </div>
      </div>
      <div className="flex justify-end gap-4 mt-6">
        <button
          type="button"
          onClick={() => setShowCodebtorModal(false)}
          className="text-gray-600 hover:underline"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setShowCodebtorModal(false)}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          Save Co-Debtor
        </button>
      </div>
    </div>
  </div>
)}

      {paymentModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">
          Payments for {paymentCreditor.account_name}
        </h2>
        <button onClick={closePaymentsModal} className="text-gray-500">&times;</button>
      </div>

      {/* List existing */}
      {paymentsLoading ? (
        <p>Loading…</p>
      ) : (
        <ul className="max-h-60 overflow-auto mb-4">
          {payments.map(p => (
            <li key={p.id} className="flex justify-between items-center py-1 border-b">
              <span>
                ${Number(p.payment).toFixed(2)} on {p.payment_date}
              </span>
              <button
                onClick={() => handleDeletePayment(p.id)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Delete
              </button>
            </li>
          ))}
          {payments.length === 0 && <li className="text-gray-500">No payments yet</li>}
        </ul>
      )}

      {/* Add new */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="number"
            step="0.01"
            placeholder="Amount"
            value={newPayment.payment}
            onChange={e => setNewPayment(np => ({ ...np, payment: e.target.value }))}
            className="flex-1 border rounded px-2 py-1"
            required
          />
          <input
            type="date"
            value={newPayment.payment_date}
            onChange={e => setNewPayment(np => ({ ...np, payment_date: e.target.value }))}
            className="border rounded px-2 py-1"
            required
          />
        </div>
        <button
          type="button"
          onClick={handleAddPayment}
          className="bg-indigo-600 text-white px-4 py-1 rounded hover:bg-indigo-700"
        >
          Add Payment
        </button>
      </div>
    </div>
  </div>
)}
  {/* --- Chapter 13 only: Chapter 13 Plan Treatment -----*/}
  {caseData?.chapter === '13' && (
    <div className="col-span-4 mt-6 border-t pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-semibold">Chapter 13 Plan Treatment</h3>
        <div className="text-xs text-gray-600 mr-2">
          {hasCh13Data(editForm) ? (
            <>
              {(lc(editForm.chapter_13_secured_treatment) || '—').toUpperCase()} {editForm.chapter_13_direct_pay ? '• Direct Pay' : ''}
            </>
          ) : 'No treatment set'}
        </div>
        <button
          type="button"
          onClick={() => setShowCh13Options(!showCh13Options)}
          className="text-gray-600 hover:underline text-sm"
        >
          {showCh13Options ? 'Hide' : 'Show'} Options
        </button>
      </div>

      {showCh13Options && (
        <div className="mt-3 rounded-lg border p-4">
          <h4 className="font-semibold mb-3">Chapter 13 Treatment</h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Secured Treatment</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={lc(editForm.chapter_13_secured_treatment)}
                onChange={(e) => setEditForm({ ...editForm, chapter_13_secured_treatment: lc(e.target.value) })}
              >
                <option value="">— Select —</option>
                <option value="maintenance">Maintenance</option>
                <option value="cramdown">Cramdown</option>
                <option value="910">910</option>
                <option value="lien">Lien Strip</option>
                <option value="surrender">Surrender</option>
                <option value="third">Third Party</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="mr-3 text-sm font-medium">Direct Pay</label>
              <input
                type="checkbox"
                className="h-5 w-5"
                checked={!!editForm.chapter_13_direct_pay}
                onChange={(e) => setEditForm({ ...editForm, chapter_13_direct_pay: e.target.checked })}
              />
            </div>

            {lc(editForm.chapter_13_secured_treatment) === 'other' && (
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-1">Other Description</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_other_description || ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_other_description: e.target.value })}
                />
              </div>
            )}
          </div>

         {/* Maintenance */}
          {lc(editForm.chapter_13_secured_treatment) === 'maintenance' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Regular Monthly Payment</label>
                <input
                  type="number" step="0.01"
                  className="w-full border rounded px-3 py-2 text-right"
                  value={editForm.chapter_13_maintenance_payment_amount ?? ''}
                  onChange={(e) =>
                    setEditForm({ ...editForm, chapter_13_maintenance_payment_amount: e.target.value })
                  }
                />
              </div>
              <div>
              <label className="block text-sm font-medium mb-1">Arrearage (Principal)</label>
              <input
                type="number" step="0.01"
                className="w-full border rounded px-3 py-2 text-right"
                value={editForm.arrearage ?? ''}
                onChange={(e) => {
                  const arrears = e.target.value;
                  const rate   = numOr(editForm.chapter_13_maintenance_arrearage_interest_rate, 0);
                  const months = numOr(editForm.chapter_13_maintenance_arrearage_months, 0);
                  const pay    = computeMonthlyPayment(arrears, rate, months);
                  setEditForm({
                    ...editForm,
                    arrearage: arrears,
                    chapter_13_maintenance_arrearage_payment: toMoney(pay),
                  });
                }}
              />
            </div>
            <div>
  <label className="block text-sm font-medium mb-1">Arrearage Interest %</label>
  <input
    type="number" step="0.01"
    className="w-full border rounded px-3 py-2 text-right"
    value={editForm.chapter_13_maintenance_arrearage_interest_rate ?? ''}
    onChange={(e) => {
      const rate   = e.target.value;
      const prin   = numOr(editForm.arrearage, 0);
      const months = numOr(editForm.chapter_13_maintenance_arrearage_months, 0);
      const pay    = computeMonthlyPayment(prin, rate, months);
      setEditForm({
        ...editForm,
        chapter_13_maintenance_arrearage_interest_rate: rate,
        chapter_13_maintenance_arrearage_payment: toMoney(pay),
      });
    }}
  />
</div>
<div>
  <label className="block text-sm font-medium mb-1">Arrearage Months</label>
  <input
    type="number"
    className="w-full border rounded px-3 py-2 text-right"
    value={editForm.chapter_13_maintenance_arrearage_months ?? ''}
    onChange={(e) => {
      const months = e.target.value;
      const prin   = numOr(editForm.arrearage, 0);
      const rate   = numOr(editForm.chapter_13_maintenance_arrearage_interest_rate, 0);
      const pay    = computeMonthlyPayment(prin, rate, months);
      setEditForm({
        ...editForm,
        chapter_13_maintenance_arrearage_months: months,
        chapter_13_maintenance_arrearage_payment: toMoney(pay),
      });
    }}
  />
</div>
              <div>
  <label className="block text-sm font-medium mb-1">Arrearage Payment (auto)</label>
  <input
    type="number" step="0.01"
    className="w-full border rounded px-3 py-2 text-right"
    value={editForm.chapter_13_maintenance_arrearage_payment ?? ''}
    onChange={(e) =>
      setEditForm({ ...editForm, chapter_13_maintenance_arrearage_payment: e.target.value })
    }
  />
</div>
            </div>
          )}

          {/* Cramdown */}
          {lc(editForm.chapter_13_secured_treatment) === 'cramdown' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Secured Amount</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_cramdown_secured_amount ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_cramdown_secured_amount: e.target.value })}
                    onBlur={(e) => {
                      const clamped = clampToBalance(e.target.value, editForm);
                      setEditForm({ ...editForm, chapter_13_cramdown_secured_amount: toMoney(clamped) });
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded"
                    onClick={() => {
                      const { totalValue } = sumLinkedAssetValues(editForm.asset_links || editingCreditor?.asset_links || [], caseData);
                      const clamped = clampToBalance(totalValue, editForm);
                      setEditForm({ ...editForm, chapter_13_cramdown_secured_amount: toMoney(clamped) });
                    }}
                  >
                    link
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Interest %</label>
                <input
                  type="number" step="0.01"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_cramdown_interest_rate ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_cramdown_interest_rate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Months</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_cramdown_months ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_cramdown_months: e.target.value })}
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-1">Payment (auto)</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_cramdown_payment ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_cramdown_payment: e.target.value })}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded"
                    onClick={() => {
                      const base = numOr(editForm.chapter_13_910_secured_amount, Number(editForm.balance) || 0);
                      const pay = computeMonthlyPayment(base, editForm.chapter_13_cramdown_interest_rate, editForm.chapter_13_cramdown_months);
                      setEditForm({ ...editForm, chapter_13_cramdown_payment: toMoney(pay) });
                    }}
                    title="Amortize secured amount (or balance) over months at interest %"
                  >
                    calc
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 910 */}
          {lc(editForm.chapter_13_secured_treatment) === '910' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Secured Amount</label>
                <input
                  type="number" step="0.01"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_910_secured_amount ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_910_secured_amount: e.target.value })}
                  onBlur={(e) => {
                    const clamped = clampToBalance(e.target.value, editForm);
                    setEditForm({ ...editForm, chapter_13_910_secured_amount: toMoney(clamped) });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Interest %</label>
                <input
                  type="number" step="0.01"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_910_interest_rate ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_910_interest_rate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Months</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_910_months ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_910_months: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment (auto)</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_910_payment ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_910_payment: e.target.value })}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded bg-indigo-300"
                    onClick={() => {
                      const base = Number(editForm.chapter_13_910_secured_amount) || Number(editForm.balance) || 0;
                      const pay = computeMonthlyPayment(base, editForm.chapter_13_910_interest_rate, editForm.chapter_13_910_months);
                      setEditForm({ ...editForm, chapter_13_910_payment: toMoney(pay) });
                    }}
                  >
                    calc
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lien Strip */}
          {lc(editForm.chapter_13_secured_treatment) === 'lien' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Other Lien Amount (auto)</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_lien_other_lien_amount ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_lien_other_lien_amount: e.target.value })}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded"
                    onClick={() => {
                      try {
                        const others = findOtherLienholders(creditors, editingCreditor || editForm);
                        const sumOtherBalances = others.reduce((s, c) => s + (Number(c.balance) || 0), 0);
                        setEditForm({ ...editForm, chapter_13_lien_other_lien_amount: toMoney(sumOtherBalances) });
                      } catch (e) {
                        console.error('Auto-compute other liens failed', e);
                      }
                    }}
                    title="Sum balances of older creditors on same asset(s)"
                  >
                    Auto
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Secured Amount (auto)</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_lien_secured_amount ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_lien_secured_amount: e.target.value })}
                    onBlur={(e) => {
                      const bal = Number(editForm.balance) || 0;
                      const num = Number(e.target.value) || 0;
                      const clamped = Math.min(Math.max(0, num), bal);
                      setEditForm({ ...editForm, chapter_13_lien_secured_amount: toMoney(clamped) });
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded"
                    onClick={() => {
                      const links = editForm.asset_links || editingCreditor?.asset_links || [];
                      const { totalValue, totalExemption } = sumLinkedAssetValues(links, caseData);
                      console.log(totalExemption)
                      const others = findOtherLienholders(creditors, editingCreditor || editForm);
                      const sumOtherBalances = others.reduce((s, c) => s + (Number(c.balance) || 0), 0);

                      const myBal = Number(editForm.balance) || 0;

                      // secured = value - other liens - exemptions
                      const securedRaw = totalValue - sumOtherBalances - totalExemption;
                      const securedPos = Math.max(0, securedRaw);
                      const securedClamped = Math.min(securedPos, Number(editForm.balance) || 0);

                      setEditForm({
                        ...editForm,
                        chapter_13_lien_secured_amount: toMoney(securedClamped),
                        chapter_13_lien_other_lien_amount: toMoney(sumOtherBalances),
                      });
                    }}
                    title="Asset value minus other liens minus exemption (not below 0)"
                  >
                    Auto
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Interest %</label>
                <input
                  type="number" step="0.01"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_lien_interest_rate ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_lien_interest_rate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Months</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.chapter_13_lien_months ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, chapter_13_lien_months: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment (auto)</label>
                <div className="flex gap-2">
                  <input
                    type="number" step="0.01"
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_lien_payment ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_lien_payment: e.target.value })}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 border rounded bg-blue"
                    onClick={() => {
                      const base = numOr(editForm.chapter_13_lien_secured_amount, Number(editForm.balance) || 0);
                      const pay = computeMonthlyPayment(base, editForm.chapter_13_lien_interest_rate, editForm.chapter_13_lien_months);
                      setEditForm({ ...editForm, chapter_13_lien_payment: toMoney(pay) });
                    }}
                  >
                    calc
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Surrender / Third party – minimal fields */}
          {(lc(editForm.chapter_13_secured_treatment) === 'surrender' || lc(editForm.chapter_13_secured_treatment) === 'third') && (
            <div className="mt-4">
              {lc(editForm.chapter_13_secured_treatment) === 'third' && (
                <>
                  <label className="block text-sm font-medium mb-1">Third Party</label>
                  <input
                    className="w-full border rounded px-3 py-2"
                    value={editForm.chapter_13_third_party || ''}
                    onChange={(e) => setEditForm({ ...editForm, chapter_13_third_party: e.target.value })}
                  />
                </>
              )}
              {lc(editForm.chapter_13_secured_treatment) === 'surrender' && (
                <p className="text-sm text-gray-600">This claim will be surrendered.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )}
        {/* Linked Assets Section */}
        <div className="col-span-3 mt-6 border-t pt-4">
          <h3 className="text-md font-semibold mb-2">Linked Assets</h3>
          {editingCreditor.asset_links && editingCreditor.asset_links.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {editingCreditor.asset_links.map(link => (
                <li key={link.id} className="flex justify-between items-center gap-3">
                  <span>{describeLinkedAsset(link)}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteAssetLink(link.id)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Unlink
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">No assets linked.</p>
          )}
          {lc(editingCreditor.debt_type) === "secured" && [
            "auto",
            "mortgage",
            "judgment lien",
            "statutory lien",
            "other",
          ].includes(lc(editingCreditor.account_type)) && (
            <button
              type="button"
              onClick={() => {
                const creditorToLink = editingCreditor;
                closeEditModal();
                setSelectedCreditor(creditorToLink);
                setShowLinkAssetModal(true);
              }}
              className="mt-3 text-sm font-medium text-indigo-600 hover:underline"
            >
              Link {editingCreditor.asset_links?.length ? "Another Asset" : "an Asset"}
            </button>
          )}
        </div>

        {/* ───── Footer Buttons ───── */}
        <div className="col-span-3 flex justify-end gap-4 mt-4">
          <button
            type="button"
            onClick={closeEditModal}
            className="text-gray-600 hover:underline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={editSaving}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {editSaving ? "Updating…" : "Update Creditor"}
          </button>
        </div>
      </form>
    </div>
  </div>
  
)}

      {creditors.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[40vh] text-center text-gray-700 space-y-4">
        <div>
          <p className="text-lg font-medium">Could not load creditors</p>
          <p className="text-sm text-gray-500 mt-1">Please refresh your browser.</p>
        </div>
      </div>
      ) : (
        <div>
            <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-3">
  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={allSelected}
      onChange={handleToggleSelectAll}
      className="h-4 w-4 border-gray-300 rounded"
    />
    Select all
  </label>

  <button
    onClick={bulkDeleteSelected}
    disabled={selectedIds.length === 0}
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm ${
      selectedIds.length === 0
        ? 'bg-gray-200 text-gray-400 border-gray-300'
        : 'bg-red-600 text-white border-red-700 hover:bg-red-700'
    }`}
    title="Delete selected creditors"
  >
    <Trash2 className="w-4 h-4" /> Delete Selected
  </button>

  {selectedIds.length > 0 && (
    <button
      onClick={clearSelection}
      className="text-sm text-gray-600 underline"
    >
      Clear selection ({selectedIds.length})
    </button>
  )}
</div>
                <span className="text-sm font-medium text-gray-700">Sort by:</span>
                <div className="relative">
                <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 text-sm rounded-md pl-3 pr-8 py-1.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="name">Name</option>
                    <option value="date">Date Opened</option>
                    <option value="balance">Balance</option>
                </select>
                <div className="absolute right-2 top-2.5 text-gray-400 pointer-events-none">
                    <ChevronDown className="w-4 h-4" />
                </div>
                </div>
                <button
                onClick={() => setSortAsc(!sortAsc)}
                className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-indigo-50 transition-all"
                title="Toggle sort direction"
                >
                {sortAsc ? (
                    <ChevronUp className="w-4 h-4 text-indigo-600" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-indigo-600" />
                )}
                </button>
            </div>
            </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleCreditors.map(c => {
          const streetVal = c.street || c.street1 || "";
            const lookupKey = [
              normalizeText(c.account_name),
              normalizeText(streetVal),
              normalizeText(c.street2),
              normalizeText(c.city),
              normalizeText(c.state),
              normalizeZip(c.zip_code),
            ].join("|");
            //console.log(lookupKey)
            const id = commonMap[lookupKey];
            const isCommon = Boolean(id);


          return (
              <div
                key={c.id}
                className={`relative p-4 rounded-lg shadow border transition-opacity hover:bg-gray-50 cursor-pointer ${
                  deletingIds.includes(c.id) ? 'opacity-50 pointer-events-none' : 'bg-white'
                }`}
                onClick={e => {
                  // avoid opening when clicking buttons inside
                  if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
                  if (e.target.type === 'checkbox' || e.target.closest('input[type="checkbox"]')) return;
                  openEditModal(c);
                }}
              >
                {/* Spinner overlay while deleting */}
                {deletingIds.includes(c.id) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <svg
                        className="animate-spin h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        />
                        <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                        />
                    </svg>
                    </div>
           )}
            <button
                onClick={() => deleteCreditor(c.id)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                title="Delete"
            >
                <Trash2 size={18} />
            </button>
            <label className="absolute bottom-2 right-2 inline-flex items-center">
              <input
                type="checkbox"
                checked={selectedIds.includes(c.id)}
                onChange={(ev) => { ev.stopPropagation(); toggleSelect(c.id); }}
                className="h-4 w-4 border-gray-300 rounded"
                title="Select creditor"
              />
            </label>
            <div className="flex items-center space-x-2 mb-1">
              <button
                onClick={() => toggleCommon(c)}
                className="p-1"
                title={isCommon ? "Remove from frequent" : "Add to frequent"}
              >
                {isCommon
                  ? <SolidStar className="w-5 h-5 text-yellow-500" />
                  : <OutlineStar className="w-5 h-5 text-gray-300" />}
              </button>
              <h2 className="text-md font-semibold">{c.account_name}</h2>
            </div>
            <p className="text-sm text-gray-600">Type: {c.account_type}</p>
            {c.asset_links && c.asset_links.length > 0 && (
            <p className="text-sm text-gray-600">
                Collateral: {
                c.asset_links.map(describeLinkedAsset).join(", ")
                }
            </p>
            )}
            <p className="text-sm text-gray-600">Who Owes: {getWhoOwesLabel(c.who_owes)}</p>
            <p className="text-sm text-gray-600">Balance: ${c.balance}</p>
            <p className="text-sm text-gray-600">Opened: {c.date_opened}</p>
            <p className="text-sm text-gray-600">Address: {c.street1} {c.city} {c.state} {c.zip_code}</p>
            </div>
          );
        })}
        {showLinkAssetModal && selectedCreditor && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200">
      <h2 className="text-lg font-bold mb-4 text-gray-800">
        Link Asset to Creditor: <span className="text-indigo-600">{selectedCreditor.account_name}</span>
      </h2>
      {selectedAccountType === "auto" && (
        <>
          <p className="text-sm text-gray-700 mb-2">
            A creditor account was indicated as an <strong>auto loan</strong>. Please link it to the correct vehicle.
          </p>
          <p className="text-sm text-gray-400 mt-1">
        <strong>Balance:</strong> ${Number(selectedCreditor.balance).toLocaleString()}
        </p>
        <p className="text-sm text-gray-400 mt-1">
        <strong>Date Opened:</strong> {new Date(selectedCreditor.date_opened).toLocaleDateString()}
        </p>
          <label className="block text-sm font-medium mb-1">Select Vehicle</label>
          <select
            value={selectedAssetId}
            onChange={(e) => {
              setSelectedAssetId(e.target.value);
              setSelectedAssetType("vehicle");
            }}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">-- Select a vehicle --</option>
            {(caseData?.vehicles || []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.year} {v.make} {v.model}
              </option>
            ))}
          </select>

          <div className="flex items-center justify-between text-sm mb-4">
            <span className="text-gray-600">Vehicle not listed?</span>
            <button
              onClick={() => setShowAddVehicleForm(true)}
              className="text-indigo-600 font-medium hover:underline"
            >
              Add Vehicle
            </button>
          </div>
          {showAddVehicleForm && (
            <div className="space-y-2 mb-4 bg-gray-50 p-3 rounded border">
                <input
                type="text"
                placeholder="Year"
                required
                className="w-full border px-2 py-1 rounded"
                value={newVehicle.year}
                onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                inputMode="numeric"
                />
                <input
                type="text"
                placeholder="Make"
                required
                className="w-full border px-2 py-1 rounded"
                value={newVehicle.make}
                onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                />
                <input
                type="text"
                placeholder="Model"
                required
                className="w-full border px-2 py-1 rounded"
                value={newVehicle.model}
                onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                />
                <input
                type="text"
                placeholder="Approx. Mileage"
                required
                className="w-full border px-2 py-1 rounded"
                value={newVehicle.mileage}
                onChange={(e) => setNewVehicle({ ...newVehicle, mileage: e.target.value })}
                inputMode="numeric"
                />
                <label className="block text-sm font-medium">Who owns this vehicle?</label>
                <select
                value={newVehicle.ownership}
                onChange={(e) => setNewVehicle({ ...newVehicle, ownership: e.target.value })}
                className="w-full border rounded px-2 py-1 mb-2"
                >
                <option value="debtor1">{debtor1Name}</option>
                {debtor2 && <option value="debtor2">{debtor2Name}</option>}
                {debtor2 && <option value="d1_d2">{debtor1Name} and {debtor2Name}</option>}
                <option value="joint_third">{debtor1Name} and a third party</option>
                </select>
                <button
                className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                onClick={async () => {
                    if (!newVehicle.make.trim() || !newVehicle.model.trim()) {
                        alert("Make and model are required.");
                        return;
                      }
                    const mileageNum = parseInt(newVehicle.mileage, 10);
                    if (isNaN(mileageNum) || mileageNum < 0) {
                    alert("Mileage must be a positive number.");
                    return;
                    }
                    const yearNum = parseInt(newVehicle.year, 10);
                    const nextYear = new Date().getFullYear() + 1;

                    if (isNaN(yearNum) || yearNum < 1930 || yearNum > nextYear) {
                    alert(`Enter a valid year.`);
                    return;
                    }
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/add-vehicle/`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(newVehicle),
                    });
                    if (res.ok) {
                    const newV = await res.json();
                    setCaseData(prev => ({ ...prev, vehicles: [...prev.vehicles, newV] }));
                    setSelectedAssetId(newV.id);
                    setSelectedAssetType("vehicle");
                    setShowAddVehicleForm(false);
                    } else {
                    alert("Failed to add vehicle.");
                    }
                }}
                >
                Save Vehicle
                </button>
            </div>
            )}
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">
                If this account is not secured, select why:
            </label>
            <select
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full border rounded px-3 py-2"
            >
                <option value="">-- Choose a reason --</option>
                <option value="paid">Paid in full</option>
                <option value="deficiency">Deficiency after repo/foreclosure</option>
                <option value="mistake">Mistakenly listed as secured</option>
            </select>

            {declineReason && (
                <button
                    disabled={!declineReason || updatingType}
                    onClick={() => handleChangeAccountType(declineReason)}
                    className="mt-3 w-full bg-yellow-500 text-white font-medium py-2 rounded hover:bg-yellow-600 transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                    {updatingType && <span className="inline-block h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin" />}
                    Update Account
                    </button>
            )}
            </div>
        </>
      )}

      {realPropertyAccountTypes.includes(selectedAccountType) && (
        <>
          <p className="text-sm text-gray-700 mb-2">
            This account was indicated as a <strong>{selectedAccountType}</strong>. Please link it to the correct real property.
          </p>
          <p className="text-sm text-gray-400 mt-1">
        <strong>Balance:</strong> ${Number(selectedCreditor.balance).toLocaleString()}
        </p>
        <p className="text-sm text-gray-400 mt-1">
        <strong>Date Opened:</strong> {new Date(selectedCreditor.date_opened).toLocaleDateString()}
        </p>
          <label className="block text-sm font-medium mb-1">Select Property</label>
          <select
            value={selectedAssetId}
            onChange={(e) => {
              setSelectedAssetId(e.target.value);
              setSelectedAssetType("realproperty");
            }}
            className="w-full border rounded px-3 py-2 mb-2"
          >
            <option value="">-- Select a property --</option>
            {(caseData.real_properties || []).map((prop) => (
              <option key={prop.id} value={prop.id}>
                {prop.street1}
              </option>
            ))}
          </select>

          <div className="flex items-center justify-between text-sm mb-4">
            <span className="text-gray-600">Property not listed?</span>
            <button
              onClick={() => setShowAddPropertyForm(true)}
              className="text-indigo-600 font-medium hover:underline"
            >
              Add Property
            </button>
            
          </div>
          {showAddPropertyForm && (
            <div className="space-y-2 mb-4 bg-gray-50 p-3 rounded border">
                <input
                type="text"
                placeholder="Street"
                className="w-full border px-2 py-1 rounded"
                value={newProperty.street1}
                onChange={(e) => setNewProperty({ ...newProperty, street1: e.target.value })}
                />
                <input
                type="text"
                placeholder="City"
                className="w-full border px-2 py-1 rounded"
                value={newProperty.city}
                onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })}
                />
                <input
                type="text"
                placeholder="State"
                className="w-full border px-2 py-1 rounded"
                value={newProperty.state}
                onChange={(e) => setNewProperty({ ...newProperty, state: e.target.value })}
                />
                <input
                type="text"
                placeholder="Zip"
                className="w-full border px-2 py-1 rounded"
                value={newProperty.zip_code}
                onChange={(e) => setNewProperty({ ...newProperty, zip_code: e.target.value })}
                inputMode="numeric"
                />
                <label className="block text-sm font-medium">Who owns this property?</label>
                <select
                value={newProperty.ownership}
                onChange={(e) => setNewProperty({ ...newProperty, ownership: e.target.value })}
                className="w-full border rounded px-2 py-1 mb-2"
                >
                <option value="debtor1">{debtor1Name}</option>
                {debtor2 && <option value="debtor2">{debtor2Name}</option>}
                {debtor2 && <option value="d1_d2">{debtor1Name} and {debtor2Name}</option>}
                <option value="joint_third">{debtor1Name} and a third party</option>
                </select>
                <button
                className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                onClick={async () => {
                    const check_full_address = {
                      street: newProperty.street1,
                      street2: newProperty.street2 || "",
                      zip: newProperty.zip_code,
                      city: newProperty.city,
                      state: newProperty.state,
                    };
                  
                    const validated = await validateAddressFields(check_full_address);
                    let updatedProperty = {}
                    if (!validated) {
                      updatedProperty = {
                        ...newProperty,
                        street1: newProperty.street1,
                        city: newProperty.city,
                        state: newProperty.state,
                        zip_code: newProperty.zip_code,
                      };
                    }
                    else {
                      const addr = validated;
                      updatedProperty = {
                        ...newProperty,
                        street1: addr.streetAddressAbbreviation || newProperty.street1,
                        city: addr.city || newProperty.city,
                        state: addr.state  || newProperty.state,
                        zip_code: addr.ZIPCode || newProperty.zip_code,
                      };
                  }
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/add-property/`, {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(updatedProperty),
                    });
                  
                    if (res.ok) {
                      const newP = await res.json();
                      setCaseData(prev => ({ ...prev, real_properties: [...prev.real_properties, newP] }));
                      setSelectedAssetId(newP.id);
                      setSelectedAssetType("realproperty");
                      setShowAddPropertyForm(false);
                    } else {
                      alert("Failed to add property.");
                    }
                  }}
                >
                Save Property
                </button>
            </div>
            )}

          <label className="block text-sm font-medium mb-1">Not a secured debt?</label>
          <select
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-4"
          >
            <option value="">-- Select reason --</option>
            <option value="paid">Paid in full</option>
            <option value="deficiency">Deficiency after foreclosure</option>
            <option value="mistake">Mistakenly listed as secured</option>
          </select>
          {declineReason && (
                <button
                    disabled={!declineReason || updatingType}
                    onClick={() => handleChangeAccountType(declineReason)}
                    className="mt-3 w-full bg-yellow-500 text-white font-medium py-2 rounded hover:bg-yellow-600 transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                    {updatingType && <span className="inline-block h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin" />}
                    Update Account
                    </button>
            )}
        </>
      )}

      {selectedAccountType === "other" && (
        <>
          <p className="text-sm text-gray-700 mb-2">
            Select the property that secures this account. This list includes household goods,
            deposit accounts such as CDs, financial assets, business property, farm property,
            and other property reported by the debtor.
          </p>
          <p className="text-sm text-gray-400 mt-1">
            <strong>Balance:</strong> ${Number(selectedCreditor.balance || 0).toLocaleString()}
          </p>
          {selectedCreditor.date_opened && (
            <p className="text-sm text-gray-400 mt-1 mb-3">
              <strong>Date Opened:</strong> {new Date(selectedCreditor.date_opened).toLocaleDateString()}
            </p>
          )}

          <label className="block text-sm font-medium mb-1">Select Other Property</label>
          <select
            value={selectedAssetType && selectedAssetId ? `${selectedAssetType}:${selectedAssetId}` : ""}
            onChange={(e) => {
              const [assetType, objectId] = e.target.value.split(":");
              setSelectedAssetType(assetType || "");
              setSelectedAssetId(objectId || "");
            }}
            disabled={linkableAssetsLoading}
            className="w-full border rounded px-3 py-2 mb-2 disabled:bg-gray-100"
          >
            <option value="">
              {linkableAssetsLoading ? "Loading property…" : "-- Select property --"}
            </option>
            {Object.entries(otherAssetGroups).map(([groupLabel, assets]) => (
              <optgroup key={groupLabel} label={groupLabel}>
                {assets.map((asset) => (
                  <option
                    key={`${asset.asset_type}:${asset.object_id}`}
                    value={`${asset.asset_type}:${asset.object_id}`}
                  >
                    {asset.label}
                    {asset.value !== null && asset.value !== undefined
                      ? ` — $${Number(asset.value).toLocaleString()}`
                      : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {!linkableAssetsLoading && otherLinkableAssets.length === 0 && !linkableAssetsError && (
            <p className="rounded bg-amber-50 p-2 text-sm text-amber-800">
              No other property has been entered for this case yet. Add the property in the
              Assets section, then return here to link it.
            </p>
          )}
          {linkableAssetsError && (
            <p className="rounded bg-red-50 p-2 text-sm text-red-700">
              {linkableAssetsError}
            </p>
          )}
        </>
      )}

      <div className="flex justify-between items-center mt-4">
      <button
        disabled={!selectedAssetId || !selectedAssetType || linking}
        onClick={handleLinkAsset}
        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
        {linking && <span className="inline-block h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin"></span>} Link Asset
        </button>


        <button
          onClick={() => {
            setShowLinkAssetModal(false);
            setSelectedCreditor(null);
            setSelectedAssetId("");
            setSelectedAssetType("");
            setDeclineReason("");
          }}
          className="text-sm text-gray-500 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
        </div>
        </div>
      )}
      {String(caseData?.chapter || '') === '13' && (caseData?.status === 'filed' || caseData?.case_number) && (
  <ProofOfClaimSection
    caseId={caseId}
    caseData={caseData}
    creditors={creditors}
    onCreditorCreated={fetchCreditors}
  />
)}
    </div>
    
  );
}
